import { prevPeriod, isMultiMonth } from "@/lib/prevPeriod";
import { useSharedKpiFilters } from "@/lib/kpiFilterMemory";
import { CreateReportButton } from "@/components/CreateReportButton";
import { PageActions, scrollToAiBlock } from "@/components/PageActions";
import { LastImportInfo } from "@/components/LastImportInfo";
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { api } from "@/lib/api";
import { toParams } from "@/lib/kpi";
import { Link, useLocation } from "react-router-dom";
import EtpAnalysisWidget from "@/pages/rh/EtpAnalysisWidget";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, Legend,
  CartesianGrid, PieChart, Pie, Cell, ComposedChart, ScatterChart, Scatter, ZAxis, ReferenceLine,
  RadialBarChart, RadialBar,
} from "recharts";
import {
  Users, Clock, Euro, TrendingUp, Heart, Target, Download, Upload,
  Settings, Search, X as XIcon, ChevronDown, LayoutGrid, Award, AlertTriangle,
  UserPlus, RotateCcw, Play, Calculator, PieChart as PieIcon, Trophy, Bell, Zap,
} from "lucide-react";
import { List as RwList } from "react-window";
import { toast } from "sonner";
import { KpiTableToolbar, SortHeadCell, useKpiSort } from "@/components/kpi/KpiTableToolbar";
import { WeekDrillDrawer } from "@/components/kpi/WeekDrillDrawer";
import FilterMultiSelect, { SearchSelect } from "@/components/filters/FilterMultiSelect";
import SimulateurLanding from "@/pages/rh/simulateur/SimulateurLanding";
import ProductiviteSimulator from "@/pages/rh/simulateur/ProductiviteSimulator";
import ProdAnalyse from "@/pages/rh/prodanalyse/ProdAnalyse";
import EmbaucheSimulator from "@/pages/rh/simulateur/EmbaucheSimulator";
import HeuresSupSimulator from "@/pages/rh/simulateur/HeuresSupSimulator";
import { ArrowLeft } from "lucide-react";
import CustomDates from "@/components/filters/CustomDates";

import { OrbitAI } from "@/components/OrbitAI";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

/* ─── constants ──────────────────────────────────────────────────────── */
const MONTHS_FR = ["JANVIER", "FEVRIER", "MARS", "AVRIL", "MAI", "JUIN", "JUILLET", "AOUT", "SEPTEMBRE", "OCTOBRE", "NOVEMBRE", "DECEMBRE"];
const MONTH_ABBR = { JANVIER: "Jan", FEVRIER: "Fév", MARS: "Mar", AVRIL: "Avr", MAI: "Mai", JUIN: "Juin", JUILLET: "Juil", AOUT: "Août", SEPTEMBRE: "Sep", OCTOBRE: "Oct", NOVEMBRE: "Nov", DECEMBRE: "Déc" };
const CLR = { blue: "#2563EB", green: "#10B981", orange: "#F59E0B", rose: "#F43F5E", violet: "#8B5CF6", yellow: "#EAB308", muted: "#94A3B8", cyan: "#0EA5E9" };
const CONTRAT_COLORS = { CDI: "#2563EB", CDD: "#10B981", APPRENTI: "#F59E0B", ALTERNANT: "#F43F5E", EXTRA: "#8B5CF6", MASCOTTE: "#EC4899" };

/* ─── formatters ─────────────────────────────────────────────────────── */
const fmtEur = v => v == null || isNaN(v) ? "—" : Math.round(v).toLocaleString("fr-FR").replace(/\u202F/g, " ").replace(/,/g, " ") + " €";
const fmtEurShort = v => {
  if (v == null || isNaN(v)) return "—";
  return Math.round(v).toLocaleString("fr-FR").replace(/\u202F/g, " ").replace(/,/g, " ") + " €";
};
const fmtPct = (v, d = 1) => v == null || isNaN(v) ? "—" : `${(v * 100).toFixed(d).replace(".", ",")}%`;
const fmtNum = (v, d = 0) => v == null || isNaN(v) ? "—" : v.toLocaleString("fr-FR", { maximumFractionDigits: d, minimumFractionDigits: d });
const fmtH = v => v == null || isNaN(v) ? "—" : Math.round(v).toLocaleString("fr-FR").replace(/,/g, " ") + " h";
const fmtHSigned = v => {
  if (v == null || isNaN(v)) return "—";
  const rv = Math.round(v);
  if (rv === 0) return "0 h";
  const abs = Math.abs(rv).toLocaleString("fr-FR").replace(/,/g, " ");
  return (rv > 0 ? "+" : "−") + abs + " h";
};
const fmtEurH = v => v == null || isNaN(v) ? "—" : v.toFixed(2).replace(".", ",") + " €/h";

/* ─── Contract classifier from poste name ────────────────────────────── */
function classifyContrat(poste) {
  if (!poste) return "CDI";
  const p = poste.toLowerCase();
  if (/apprenti/.test(p)) return "APPRENTI";
  if (/altern/.test(p)) return "ALTERNANT";
  if (/extra|mascotte|animation/.test(p)) return "EXTRA";
  if (/cdd/.test(p)) return "CDD";
  return "CDI";
}

/* ─── MultiSelect (compact) ──────────────────────────────────────────── */
function MultiSelect({ label, options, values, onChange, testid, minWidth = "min-w-[140px]" }) {
  return <FilterMultiSelect label={label} options={options} value={values || []} onChange={onChange} testid={testid} minWidth={minWidth} size="sm" placeholder={`Tous les ${label}`}/>;
}

/* ─── KpiCard (compact avec variation) ───────────────────────────────── */
function KpiCard({ icon: Icon, iconColor = "text-blue-600", iconBg = "bg-blue-50", label, value, delta, unit = "%", testid, progress, sub, deltaLabel = "vs mois préc." }) {
  const up = delta != null && delta > 0;
  const down = delta != null && delta < 0;
  const tone = up ? "text-emerald-600" : down ? "text-rose-600" : "text-slate-500";
  return (
    <div className="bg-white rounded-xl border border-slate-200 px-3 py-2.5 shadow-[0_1px_3px_rgba(15,23,42,0.04)] min-w-0" data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[9px] font-semibold text-slate-500 uppercase tracking-wider truncate">{label}</span>
        {Icon && <div className={`w-6 h-6 rounded-md ${iconBg} grid place-items-center shrink-0`}><Icon className={`w-3 h-3 ${iconColor}`}/></div>}
      </div>
      <div className="font-bold text-slate-900 text-[17px] tabular-nums leading-tight truncate">{value}</div>
      {sub && <div className="text-[10px] mt-0.5 font-medium text-slate-500 truncate" title={sub}>{sub}</div>}
      {progress != null && (
        <div className="h-1 mt-1.5 rounded-full bg-slate-100 overflow-hidden">
          <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, Math.max(0, progress * 100))}%`, background: progress >= 1 ? CLR.green : progress >= 0.9 ? CLR.orange : CLR.blue }}/>
        </div>
      )}
      {delta != null && (
        <div className={`text-[10px] mt-1 font-semibold ${tone} tabular-nums flex items-center gap-1`}>
          <span>{up ? "↗" : down ? "↘" : "→"}</span>
          <span>{delta > 0 ? "+" : ""}{Math.abs(delta) < 10 ? delta.toFixed(1).replace(".", ",") : Math.round(delta)}{unit === "pt" ? " pt" : unit === "%" ? "%" : unit}</span>
          <span className="text-slate-400 font-normal">{deltaLabel}</span>
        </div>
      )}
    </div>
  );
}

/* ─── Seuils métier centralisés casse (cible entreprise = 10 %) ─────
   ≤ 8 %              → Très bon
   > 8 %  et ≤ 10 %   → Maîtrisé (cible entreprise)
   > 10 % et ≤ 12 %   → À surveiller
   > 12 %             → Alerte casse
   → Point unique de configuration ; le backend renvoie les mêmes seuils
     dans data.thresholds (source de vérité serveur). */
const CASSE_THRESHOLDS = {
  tres_bon:  0.08,   // ≤ 8 %
  target:    0.10,   // cible entreprise
  alert:     0.12,   // > 12 % = alerte
  prod_proche_cible: 0.95, // prod_production ≥ 95 % de la cible = "≥ cible"
};

/* Diagnostic métier — CAS A/B/C/D (aligné backend /api/kpi/productivite).
   Le backend renvoie déjà `diagnostic_label/tone/key + reco_priorite_casse` par
   corner. Cette fonction est utilisée uniquement pour les agrégats calculés
   côté client (vue Réseau/Secteur/DR + ligne Total). */
function computeDiagnostic({ prod_com, prod_production, cible, taux_casse }) {
  if (!cible || cible <= 0) return { key: "none", label: "—", tone: "slate", reco_priorite_casse: false };
  const has = prod_production != null && !isNaN(prod_production);
  if (!has) return { key: "no_data", label: "Production N/A", tone: "slate", reco_priorite_casse: false };
  const commercialAtteint = prod_com >= cible;
  const productionAtteinte = prod_production >= cible * CASSE_THRESHOLDS.prod_proche_cible;
  const casseAuDessusCible = taux_casse != null && taux_casse > CASSE_THRESHOLDS.target;   // > 10 %
  const casseAlerte        = taux_casse != null && taux_casse > CASSE_THRESHOLDS.alert;    // > 12 %
  // CAS A — commercial OK
  if (commercialAtteint) {
    if (casseAlerte) return { key: "A_ok_casse_elevee", label: "Productivité OK · ⚠️ Casse élevée", tone: "amber", reco_priorite_casse: true };
    if (casseAuDessusCible) return { key: "A_ok_casse_surveiller", label: "Productivité OK · Casse à surveiller", tone: "amber", reco_priorite_casse: false };
    return { key: "A_ok", label: "Productivité OK", tone: "emerald", reco_priorite_casse: false };
  }
  // CAS B — commercial KO, production OK, casse > cible entreprise
  if (productionAtteinte && casseAuDessusCible) {
    return { key: "B_prod_ok_casse_a_corriger", label: "⚠️ Production suffisante · Casse à corriger", tone: "rose", reco_priorite_casse: true };
  }
  // CAS C — commercial KO, production KO
  if (!productionAtteinte) {
    return { key: "C_operationnel_insuffisant", label: "Productivité opérationnelle insuffisante", tone: "orange", reco_priorite_casse: false };
  }
  // CAS D — commercial KO, production OK, casse ≤ cible entreprise
  return { key: "D_ecart_prod_ca", label: "Écart Production / CA à analyser", tone: "amber", reco_priorite_casse: false };
}

/* ── Normalisation FR : casse + accents (pour comparer les mois RAW) ── */
const _foldFR = (s) => (s == null ? "" : String(s).normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase().trim());

/* ─── FilterBar (DR / Secteur / Corner / Exercice / Mois / Semaine) ──── */
function MultiSelectPill({ label, options, value, onChange, testid, minWidth = 110 }) {
  const cap = s => typeof s === "string" ? s.charAt(0) + s.slice(1).toLowerCase() : s;
  return <FilterMultiSelect label={label} options={(options || []).map(o => ({ value: o, label: cap(o) }))} value={value || []} onChange={onChange} testid={testid} minWidth={minWidth} size="sm" placeholder="Tous" menuWidth={200}/>;
}

export function FilterBar({ filters, f, setF, exercicesFromData, showSemaine = true, showAnnee = false }) {
  const drs = filters?.drs || [];
  const secteurs = filters?.secteurs || [];
  const semainesAll = filters?.semaines || [];
  const corners = (filters?.corners || [])
    .filter(c => !f.drs?.length || f.drs.includes(c.dr))
    .filter(c => !f.secteurs?.length || f.secteurs.includes(c.secteur))
    .map(c => c.corner).filter(Boolean);
  const cornerOpts = Array.from(new Set(corners));
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-3 flex flex-wrap items-end gap-2" data-testid="rh-filter-bar">
      <div className="flex flex-col">
        <span className="text-[9px] font-semibold uppercase tracking-wider text-slate-400 mb-0.5">DR</span>
        <MultiSelect label="DR" testid="rh-flt-dr" options={drs} values={f.drs}
                     onChange={v => setF(o => ({
                       ...o, drs: v,
                       secteurs: (o.secteurs || []).filter(s => !v.length || (filters?.corners || []).some(c => c.secteur === s && v.includes(c.dr))),
                       corners: (o.corners || []).filter(cn => !v.length || (filters?.corners || []).some(c => c.corner === cn && v.includes(c.dr))),
                     }))}/>
      </div>
      <div className="flex flex-col">
        <span className="text-[9px] font-semibold uppercase tracking-wider text-slate-400 mb-0.5">Secteur</span>
        <MultiSelect label="Secteur" testid="rh-flt-secteur" options={secteurs} values={f.secteurs || []}
                     onChange={v => setF(o => ({
                       ...o, secteurs: v,
                       corners: (o.corners || []).filter(cn => !v.length || (filters?.corners || []).some(c => c.corner === cn && v.includes(c.secteur))),
                     }))}/>
      </div>
      <div className="flex flex-col flex-1 min-w-[180px]">
        <span className="text-[9px] font-semibold uppercase tracking-wider text-slate-400 mb-0.5">Corners</span>
        <MultiSelect label="corners" testid="rh-flt-corner" options={cornerOpts} values={f.corners} onChange={v => setF(o => ({ ...o, corners: v }))} minWidth="min-w-full"/>
      </div>
      {showAnnee && (
        <div className="flex flex-col">
          <span className="text-[9px] font-semibold uppercase tracking-wider text-slate-400 mb-0.5">Année</span>
          <FilterMultiSelect testid="rh-flt-annee" label="Année" placeholder="Toutes" minWidth={100} menuWidth={140}
            options={(filters?.annees || []).map(String)}
            value={(f.annees || []).map(String)}
            onChange={arr => setF(o => ({ ...o, annees: arr.map(Number) }))}/>
        </div>
      )}
      <div className="flex flex-col">
        <span className="text-[9px] font-semibold uppercase tracking-wider text-slate-400 mb-0.5">Exercice</span>
        <FilterMultiSelect testid="rh-flt-exercice" label="Exercice" placeholder="Tous" minWidth={110} menuWidth={180}
          options={(filters?.exercices?.length ? filters.exercices : exercicesFromData) || []}
          value={(f.exercice || "").split(",").filter(Boolean)}
          onChange={arr => setF(o => ({ ...o, exercice: arr.length ? arr.join(",") : null }))}/>
      </div>
      <div className="flex flex-col">
        <span className="text-[9px] font-semibold uppercase tracking-wider text-slate-400 mb-0.5">Mois</span>
        <MultiSelectPill testid="rh-flt-mois"
          options={((filters?.mois?.length ? filters.mois : MONTHS_FR) || [])}
          value={f.mois || []}
          onChange={vals => setF(o => ({ ...o, mois: vals }))}/>
        <CustomDates testid="rh-flt-dates" value={f} onChange={p => setF(o => ({ ...o, ...p }))} />
      </div>
      {showSemaine && (
        <div className="flex flex-col">
          <span className="text-[9px] font-semibold uppercase tracking-wider text-slate-400 mb-0.5">Semaine</span>
          <MultiSelectPill testid="rh-flt-semaine" minWidth={90}
            options={(semainesAll.length ? semainesAll : [...Array(52)].map((_, i) => `S${String(i + 1).padStart(2, "0")}`))}
            value={f.semaines || []}
            onChange={vals => setF(o => ({ ...o, semaines: vals }))}/>
        </div>
      )}
      {(f.drs.length || (f.secteurs || []).length || f.corners.length || f.exercice || f.mois.length || (f.semaines || []).length || (f.annees || []).length) > 0 && (
        <button onClick={() => setF({ drs: [], secteurs: [], corners: [], exercice: null, mois: [], semaines: [], annees: [] })}
                data-testid="rh-flt-reset"
                className="text-[11px] text-rose-600 font-semibold hover:underline flex items-center gap-1 mb-1">
          <XIcon className="w-3 h-3"/> Réinitialiser
        </button>
      )}
    </div>
  );
}

/* ─── Aggregation utils ──────────────────────────────────────────────── */
// Stable employee key : fallback (poste + salaire_brut + taux_horaire) quand `name` absent
const empKey = (r) => r?.name || `${r?.poste || ""}|${r?.salaire_brut || ""}|${r?.taux_horaire || ""}`;

function aggRh(rows) {
  const s = rows.reduce((a, r) => {
    a.h_travaillees += r.h_travaillees || 0;
    a.h_contrat_semaine += r.h_contrat_semaine || 0;
    a.h_presence_contrat += r.h_presence_contrat || 0;
    a.hs += r.hs || 0;
    a.habs += r.habs || 0;
    a.hcp += r.hcp || 0;
    a.masse_salariale += r.masse_salariale || 0;
    a.salaire_brut += r.salaire_brut || 0;
    a.taux_horaire_sum += (r.taux_horaire || 0) * (r.h_travaillees || 0);
    // Track employees per month → moyenne d'effectif à travers la période (pas de double-comptage
    // sur la semaine, moyenne quand plusieurs mois sont dans la sélection)
    const m = r.mois || "_";
    if (!a.empsByMonth[m]) a.empsByMonth[m] = new Set();
    a.empsByMonth[m].add(empKey(r));
    a.corners.add(r.corner);
    return a;
  }, { h_travaillees: 0, h_contrat_semaine: 0, h_presence_contrat: 0, hs: 0, habs: 0, hcp: 0, masse_salariale: 0, salaire_brut: 0, taux_horaire_sum: 0, empsByMonth: {}, corners: new Set() });
  const monthCounts = Object.values(s.empsByMonth).map(set => set.size);
  const effectif = monthCounts.length ? Math.round(monthCounts.reduce((a, n) => a + n, 0) / monthCounts.length) : 0;
  return {
    ...s,
    effectif,
    n_corners: s.corners.size,
    taux_horaire_moyen: s.h_travaillees ? s.taux_horaire_sum / s.h_travaillees : 0,
    absenteisme: s.h_presence_contrat ? s.habs / s.h_presence_contrat : 0,
    taux_hs: s.h_travaillees ? s.hs / s.h_travaillees : 0,
  };
}

/* ─── Vue RH tab (own FilterBar + own /kpi/rh/rows fetch) ─────────── */
function VueRHTab() {
  const [filters, setFilters] = useState({ drs: [], secteurs: [], bms: [], corners: [], mois: [], semaines: [], exercices: [] });
  const [f, setF] = useSharedKpiFilters({ drs: [], secteurs: [], corners: [], exercice: null, mois: [], semaines: [] }, { meta: filters, pageKey: "orbit_rh_vuerh_filters" });
  const [rhRows, setRhRows] = useState([]);
  useEffect(() => {
    api.get("/kpi/rh/meta").then(r => setFilters(r.data || {})).catch(() => {});
  }, []);
  useEffect(() => {
    const params = toParams({ drs: f.drs, secteurs: f.secteurs, corners: f.corners, semaines: f.semaines, mois: f.mois });
    if (f.exercice) params.exercice = f.exercice;
    if (f.date_from) params.date_from = f.date_from;
    if (f.date_to) params.date_to = f.date_to;
    let alive = true;
    api.get("/kpi/rh/rows", { params }).then(r => { if (alive) setRhRows(r.data || []); }).catch(() => { if (alive) setRhRows([]); });
    return () => { alive = false; };
  }, [f.drs, f.secteurs, f.corners, f.exercice, f.mois, f.semaines, f.date_from, f.date_to]);
  // Période de comparaison (dates personnalisées → période précédente de même durée)
  const [prevRows, setPrevRows] = useState(null);
  const pp = prevPeriod(f);
  const ppKey = JSON.stringify(pp?.params || null);
  useEffect(() => {
    if (!pp || !isMultiMonth(f)) { setPrevRows(null); return; }
    const params = { ...toParams({ drs: f.drs, secteurs: f.secteurs, corners: f.corners }), ...pp.params };
    let alive = true;
    api.get("/kpi/rh/rows", { params }).then(r => { if (alive) setPrevRows(r.data || []); }).catch(() => { if (alive) setPrevRows(null); });
    return () => { alive = false; };
  }, [f.drs, f.secteurs, f.corners, ppKey]); // eslint-disable-line react-hooks/exhaustive-deps
  // Client-side accent-insensitive extra filter (backend already does regex match on mois but let's be safe)
  const filteredRows = useMemo(() => {
    if (!f.mois?.length) return rhRows;
    const wanted = new Set(f.mois.map(_foldFR));
    return rhRows.filter(r => wanted.has(_foldFR(r.mois)));
  }, [rhRows, f.mois]);
  // Monthly aggregation
  const monthAgg = useMemo(() => {
    const map = {};
    filteredRows.forEach(r => {
      const k = _foldFR(r.mois) || "_"; if (!k) return;
      map[k] = map[k] || { mois: k, rows: [] }; map[k].rows.push(r);
    });
    const list = Object.entries(map)
      .map(([mois, v]) => ({ mois, ...aggRh(v.rows), rows_count: v.rows.length, order: MONTHS_FR.indexOf(mois) }))
      .sort((a, b) => a.order - b.order);
    if (isMultiMonth(f)) {
      // Année / exercice / plusieurs mois / dates personnalisées : cumul de la période (= tableaux) vs période précédente équivalente
      const cur = filteredRows.length ? { mois: "PERIODE", ...aggRh(filteredRows), rows_count: filteredRows.length } : null;
      const prev = prevRows?.length ? { mois: "PERIODE_PREC", ...aggRh(prevRows), rows_count: prevRows.length } : null;
      return { list, cur, prev, deltaLabel: pp?.label || "vs période préc." };
    }
    const target = f.mois?.[0] ? _foldFR(f.mois[0]) : list[list.length - 1]?.mois;
    const idx = list.findIndex(x => x.mois === target);
    return { list, cur: idx >= 0 ? list[idx] : list[list.length - 1] || null,
             prev: idx > 0 ? list[idx - 1] : list[list.length - 2] || null, deltaLabel: "vs mois préc." };
  }, [filteredRows, f.mois, f.date_from, f.date_to, prevRows, f.exercice, f.annees]); // eslint-disable-line react-hooks/exhaustive-deps
  // Weekly aggregation (used by the HS evolution chart when semaines selected)
  const weekAgg = useMemo(() => {
    const map = {};
    filteredRows.forEach(r => {
      const w = (r.semaine || "").toString().trim(); if (!w) return;
      map[w] = map[w] || { semaine: w, rows: [] }; map[w].rows.push(r);
    });
    return Object.entries(map)
      .map(([semaine, v]) => ({ semaine, ...aggRh(v.rows) }))
      .sort((a, b) => a.semaine.localeCompare(b.semaine, undefined, { numeric: true }));
  }, [filteredRows]);
  const exercicesFromData = useMemo(() => [...new Set(rhRows.map(r => r.exercice).filter(Boolean))].sort(), [rhRows]);
  const rhRowsForTab = filteredRows;
  return (
    <div className="space-y-4">
      <FilterBar filters={filters} f={f} setF={setF} exercicesFromData={exercicesFromData}/>
      <VueRHContent rhRows={rhRowsForTab} monthAgg={monthAgg} weekAgg={weekAgg} weekMode={(f.semaines?.length || 0) > 0}/>
    </div>
  );
}

/* ─── Vue RH content (was VueRHTab) ────────────────────────────────── */
function VueRHContent({ rhRows, monthAgg, weekAgg, weekMode }) {
  // KPI cards (current vs previous month) — RH ONLY, no cross-collection data
  const cur = monthAgg.cur; const prev = monthAgg.prev;
  const kpis = useMemo(() => {
    if (!cur) return null;
    const p = prev || {};
    return [
      { key: "effectif", icon: Users, iconColor: "text-blue-600", iconBg: "bg-blue-50",
        label: "Effectif total", value: fmtNum(cur.effectif),
        delta: p.effectif != null ? cur.effectif - p.effectif : null, unit: "" },
      { key: "habs", icon: Heart, iconColor: "text-rose-600", iconBg: "bg-rose-50",
        label: "Heures absence", value: fmtH(cur.habs || 0),
        delta: p.habs ? ((cur.habs - p.habs) / p.habs) * 100 : null, unit: "%" },
      { key: "heures", icon: Clock, iconColor: "text-cyan-600", iconBg: "bg-cyan-50",
        label: "Heures travaillées", value: fmtH(cur.h_travaillees),
        delta: p.h_travaillees ? ((cur.h_travaillees - p.h_travaillees) / p.h_travaillees) * 100 : null, unit: "%" },
      { key: "ms", icon: Users, iconColor: "text-violet-600", iconBg: "bg-violet-50",
        label: "Masse salariale totale", value: fmtEur(cur.masse_salariale),
        delta: p.masse_salariale ? ((cur.masse_salariale - p.masse_salariale) / p.masse_salariale) * 100 : null, unit: "%" },
      { key: "cout-h", icon: Euro, iconColor: "text-emerald-600", iconBg: "bg-emerald-50",
        label: "Coût horaire moyen", value: fmtEurH(cur.taux_horaire_moyen),
        delta: p.taux_horaire_moyen ? ((cur.taux_horaire_moyen - p.taux_horaire_moyen) / p.taux_horaire_moyen) * 100 : null, unit: "%" },
      { key: "hs", icon: Zap, iconColor: "text-yellow-600", iconBg: "bg-yellow-50",
        label: "Heures supp", value: fmtH(cur.hs || 0),
        delta: p.hs ? ((cur.hs - p.hs) / p.hs) * 100 : null, unit: "%" },
      { key: "absent", icon: Heart, iconColor: "text-pink-600", iconBg: "bg-pink-50",
        label: "Absentéisme", value: fmtPct(cur.absenteisme),
        delta: p.absenteisme != null ? (cur.absenteisme - p.absenteisme) * 100 : null, unit: "pt" },
      { key: "etp", icon: Award, iconColor: "text-teal-600", iconBg: "bg-teal-50",
        label: "ETP moyen", value: fmtNum((() => {
          // Same logic as Effectif: group by (mois, semaine) → sum etp_semaine per week
          // → average across weeks (per month) → average across months
          const byMonth = {};
          rhRows.forEach(r => {
            const m = r.mois || "_"; const w = r.semaine || "_";
            if (!byMonth[m]) byMonth[m] = {};
            byMonth[m][w] = (byMonth[m][w] || 0) + (r.etp_semaine || 0);
          });
          const monthAvgs = Object.values(byMonth).map(weeks => {
            const vals = Object.values(weeks);
            return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
          });
          const avg = monthAvgs.length ? monthAvgs.reduce((a, b) => a + b, 0) / monthAvgs.length : 0;
          return Math.round(avg * 10) / 10;
        })()),
        delta: null },
    ];
  }, [cur, prev, rhRows]);

  // Evolution chart (HS + heures travaillées) — bascule mois/semaines selon la sélection
  // Toggle Mois/Semaine pour évolution HS
  const [hsMode, setHsMode] = useState(weekMode ? "semaine" : "mois");
  // Sync auto quand le filtre semaines change
  useEffect(() => { if (weekMode) setHsMode("semaine"); }, [weekMode]);
  const evoData = useMemo(() => {
    if (hsMode === "semaine" && weekAgg?.length) {
      return weekAgg.map(w => ({
        mois: w.semaine,   // gardé "mois" pour éviter de casser le dataKey du chart
        "Heures supp": Math.round(w.hs || 0),
        "Heures travaillées": Math.round(w.h_travaillees || 0),
      }));
    }
    return monthAgg.list.map(m => ({
      mois: MONTH_ABBR[m.mois] || m.mois,
      "Heures supp": Math.round(m.hs || 0),
      "Heures travaillées": Math.round(m.h_travaillees || 0),
    }));
  }, [monthAgg, weekAgg, hsMode]);
  // Evolution masse salariale € (nouveau widget) — support toggle Mois/Semaine
  const [msMode, setMsMode] = useState("mois"); // mois | semaine
  const evoMs = useMemo(() => {
    if (msMode === "semaine") {
      return (weekAgg || []).map(w => ({
        mois: w.semaine, // libellé S01, S02...
        "Masse salariale (€)": Math.round(w.masse_salariale || 0),
      }));
    }
    return monthAgg.list.map(m => ({
      mois: MONTH_ABBR[m.mois] || m.mois,
      "Masse salariale (€)": Math.round(m.masse_salariale || 0),
    }));
  }, [monthAgg, weekAgg, msMode]);

  // Répartition effectifs — tabs contrat / poste / région (comme Salaires)
  const [effMode, setEffMode] = useState("contrat");
  const effContrat = useMemo(() => {
    const map = {};
    const seen = new Set();
    rhRows.forEach(r => {
      const k = empKey(r); if (!k || seen.has(k)) return;
      seen.add(k);
      const dim = effMode === "contrat" ? classifyContrat(r.poste)
                : effMode === "poste"   ? (r.poste || "—")
                : (r.region || "—");
      map[dim] = (map[dim] || 0) + 1;
    });
    return Object.entries(map).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [rhRows, effMode]);
  const totalEff = effContrat.reduce((s, x) => s + x.value, 0);

  // Détail par corner (RH ONLY — no prodData crossing)
  const detailCorners = useMemo(() => {
    const map = {};
    rhRows.forEach(r => {
      const k = r.corner; if (!k) return;
      if (!map[k]) map[k] = { corner: k, region: r.region, dr: r.dr, secteur: r.secteur, bm: r.bm, enseigne: r.enseigne, rows: [] };
      map[k].rows.push(r);
    });
    const corners = Object.values(map).map(g => {
      const a = aggRh(g.rows);
      return {
        ...g, effectif: a.effectif, heures: a.h_travaillees, ms: a.masse_salariale,
        coutH: a.taux_horaire_moyen, absenteisme: a.absenteisme, hs: a.hs, habs: a.habs,
        statut: a.absenteisme < 0.03 ? "Bon" : a.absenteisme < 0.06 ? "Vigilance" : "À surveiller",
      };
    });
    return corners.sort((a, b) => (b.ms || 0) - (a.ms || 0));
  }, [rhRows]);

  // Top 5 lists — RH ONLY (no productivité)
  const top5 = useMemo(() => {
    return {
      mostMs:  [...detailCorners].sort((a, b) => (b.ms || 0) - (a.ms || 0)).slice(0, 5),
      mostHs:  [...detailCorners].sort((a, b) => (b.hs || 0) - (a.hs || 0)).slice(0, 5),
      mostAbs: [...detailCorners].sort((a, b) => (b.absenteisme || 0) - (a.absenteisme || 0)).slice(0, 5),
      leastAbs:[...detailCorners].filter(c => c.absenteisme > 0).sort((a, b) => a.absenteisme - b.absenteisme).slice(0, 5),
    };
  }, [detailCorners]);

  return (
    <div className="space-y-4">
      {/* 8 KPI cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-8 gap-2" data-testid="rh-kpi-row">
        {kpis ? kpis.map(c => <KpiCard key={c.key} {...c} deltaLabel={monthAgg.deltaLabel} testid={`rh-kpi-${c.key}`}/>) : [...Array(8)].map((_, i) => <div key={i} className="bg-white rounded-xl border border-slate-200 h-[92px] animate-pulse"/>)}
      </div>

      {/* Chart evolution HS + Répartition effectifs (tabs) + Évolution MS € */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4 xl:col-span-2" data-testid="rh-hs-evolution-chart">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <h3 className="font-bold text-slate-800 text-sm">Évolution des heures supplémentaires <span className="text-slate-500 font-normal text-[11px]">— {hsMode==="semaine" ? `${weekAgg?.length || 0} semaine(s)` : "vue mensuelle"}</span></h3>
            <div className="inline-flex items-center border border-slate-200 rounded overflow-hidden" data-testid="rh-hs-toggle">
              <button data-testid="rh-hs-mois" onClick={()=>setHsMode("mois")}
                      className={`px-2.5 py-1 text-[11px] font-semibold ${hsMode==="mois"?"bg-blue-600 text-white":"bg-white text-slate-600 hover:bg-slate-50"}`}>Mois</button>
              <button data-testid="rh-hs-semaine" onClick={()=>setHsMode("semaine")}
                      className={`px-2.5 py-1 text-[11px] font-semibold ${hsMode==="semaine"?"bg-blue-600 text-white":"bg-white text-slate-600 hover:bg-slate-50"}`}>Semaine</button>
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer>
              <ComposedChart data={evoData} margin={{ top: 6, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9"/>
                <XAxis dataKey="mois" tick={{ fontSize: 10, fill: "#64748B" }} axisLine={false} tickLine={false}/>
                <YAxis yAxisId="left" tick={{ fontSize: 10, fill: "#64748B" }} tickFormatter={v => v + "h"} axisLine={false} tickLine={false} width={55}/>
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: "#64748B" }} tickFormatter={v => Math.round(v).toLocaleString("fr-FR").replace(/,/g," ")} axisLine={false} tickLine={false} width={70}/>
                <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 6, fontSize: 12 }}/>
                <Legend wrapperStyle={{ fontSize: 11 }} iconType="rect"/>
                <Bar yAxisId="left" dataKey="Heures supp" fill="#FEF3C7" stroke="#F59E0B" strokeWidth={1} radius={[4, 4, 0, 0]}/>
                <Line yAxisId="right" type="monotone" dataKey="Heures travaillées" stroke={CLR.blue} strokeWidth={2.5} dot={{ r: 3 }}/>
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="rh-effectifs-donut">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-bold text-slate-800 text-sm">Répartition des effectifs</h3>
          </div>
          <div className="flex bg-slate-100 rounded-lg p-0.5 mb-3">
            {[{ k: "contrat", l: "Contrat" }, { k: "poste", l: "Poste" }, { k: "region", l: "Région" }].map(t => (
              <button key={t.k} onClick={() => setEffMode(t.k)}
                      data-testid={`rh-eff-tab-${t.k}`}
                      className={`flex-1 text-[10px] px-2 py-1 rounded-md font-semibold transition-colors ${effMode === t.k ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-700"}`}>
                {t.l}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <div className="w-28 h-28 relative flex-shrink-0">
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={effContrat.slice(0, 6)} dataKey="value" nameKey="name" innerRadius={32} outerRadius={52} paddingAngle={2}>
                    {effContrat.slice(0, 6).map((e, i) => <Cell key={i} fill={CONTRAT_COLORS[e.name] || `hsl(${i * 47}, 70%, 55%)`}/>)}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 grid place-items-center pointer-events-none">
                <div className="text-center">
                  <div className="text-[14px] font-bold text-slate-900 tabular-nums">{fmtNum(totalEff)}</div>
                  <div className="text-[8px] uppercase font-bold text-slate-500">collab.</div>
                </div>
              </div>
            </div>
            <div className="flex-1 space-y-1 text-[10px] max-h-[112px] overflow-y-auto pr-1">
              {effContrat.slice(0, 8).map((d, i) => (
                <div key={d.name} className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: CONTRAT_COLORS[d.name] || `hsl(${i * 47}, 70%, 55%)` }}/>
                  <span className="text-slate-700 font-medium flex-1 truncate">{d.name}</span>
                  <span className="text-slate-900 font-bold tabular-nums">{totalEff ? Math.round(d.value / totalEff * 100) : 0}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="rh-ms-evolution-chart">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <h3 className="font-bold text-slate-800 text-sm">Évolution de la masse salariale (€)</h3>
            <div className="inline-flex items-center border border-slate-200 rounded overflow-hidden" data-testid="rh-ms-toggle">
              <button data-testid="rh-ms-mois" onClick={()=>setMsMode("mois")}
                      className={`px-2.5 py-1 text-[11px] font-semibold ${msMode==="mois"?"bg-blue-600 text-white":"bg-white text-slate-600 hover:bg-slate-50"}`}>Mois</button>
              <button data-testid="rh-ms-semaine" onClick={()=>setMsMode("semaine")}
                      className={`px-2.5 py-1 text-[11px] font-semibold ${msMode==="semaine"?"bg-blue-600 text-white":"bg-white text-slate-600 hover:bg-slate-50"}`}>Semaine</button>
            </div>
          </div>
          <div className="h-56">
            <ResponsiveContainer>
              <ComposedChart data={evoMs} margin={{ top: 6, right: 6, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9"/>
                <XAxis dataKey="mois" tick={{ fontSize: 10, fill: "#64748B" }} axisLine={false} tickLine={false}/>
                <YAxis tick={{ fontSize: 10, fill: "#64748B" }} tickFormatter={fmtEurShort} axisLine={false} tickLine={false} width={50}/>
                <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 6, fontSize: 12 }} formatter={v => fmtEur(v)}/>
                <Bar dataKey="Masse salariale (€)" fill="#DDD6FE" stroke={CLR.violet} strokeWidth={1} radius={[4, 4, 0, 0]}/>
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Détail par corner (virtualized) — RH ONLY */}
      <DetailCornerTable corners={detailCorners}/>

      {/* Détail par collaborateur avec sélecteur "Vu par" — RH ONLY */}
      <CollabDetailTable rhRows={rhRows}/>

      {/* Analyse ETP */}
      <EtpAnalysisWidget monthAgg={monthAgg} weekAgg={weekAgg} rhRows={rhRows}/>

      {/* Top 5 blocks — RH ONLY */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        <Top5Card icon={Users} color="amber" title="Top 5 — Masse salariale la + élevée" items={top5.mostMs} fmt="eur" valKey="ms" testid="rh-top-most-ms"/>
        <Top5Card icon={Zap} color="orange" title="Top 5 — Heures supp les + élevées" items={top5.mostHs} fmt="h" valKey="hs" testid="rh-top-most-hs"/>
        <Top5Card icon={Heart} color="pink" title="Top 5 — Plus d'absences" items={top5.mostAbs} fmt="pct" valKey="absenteisme" testid="rh-top-most-abs"/>
        <Top5Card icon={Award} color="emerald" title="Top 5 — Meilleur absentéisme" items={top5.leastAbs} fmt="pct" valKey="absenteisme" testid="rh-top-best-abs"/>
      </div>
    </div>
  );
}

/* ─── Sub: Detail Corner Table (virtualized + sortable) ────────────── */
const DetailCornerRow = ({ index, style, rows }) => {
  const c = rows[index];
  if (!c) return null;
  const st = c.statut;
  const stCol = st === "Bon" ? "bg-emerald-100 text-emerald-800" : st === "Vigilance" ? "bg-amber-100 text-amber-800" : "bg-rose-100 text-rose-800";
  return (
    <div style={{ ...style, display: "grid", gridTemplateColumns: "1.6fr 1fr 0.7fr 1fr 0.9fr 1.2fr 0.9fr 0.9fr 0.9fr 0.9fr 0.9fr" }}
         className="items-center text-[11px] border-b border-slate-100 hover:bg-slate-50/70 px-3">
      <div className="flex items-center gap-2 min-w-0">
        <span className="w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0"/>
        <span className="font-semibold text-slate-900 truncate">{c.corner}</span>
      </div>
      <div className="text-slate-600 truncate">{c.region}</div>
      <div className="text-right tabular-nums font-semibold">{fmtNum(c.effectif)}</div>
      <div className="text-right tabular-nums">{fmtH(c.heures)}</div>
      <div className={`text-right tabular-nums ${(c.hs||0) > 0 ? "text-amber-700 font-semibold" : "text-slate-500"}`}>{fmtH(c.hs || 0)}</div>
      <div className="text-right tabular-nums font-semibold">{fmtEur(c.ms)}</div>
      <div className={`text-right tabular-nums ${(c.habs||0) > 0 ? "text-rose-600 font-semibold" : "text-slate-500"}`}>{fmtH(c.habs || 0)}</div>
      <div className="text-right tabular-nums">{fmtEurH(c.coutH)}</div>
      <div className="text-right tabular-nums font-semibold text-blue-700">{fmtEurH(c.prod)}</div>
      <div className={`text-right tabular-nums ${c.absenteisme > 0.05 ? "text-rose-600 font-semibold" : "text-slate-700"}`}>{fmtPct(c.absenteisme)}</div>
      <div className="text-right">
        <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold ${stCol}`}>{st}</span>
      </div>
    </div>
  );
};

function DetailCornerTable({ corners }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    if (!qq) return corners;
    return corners.filter(c => (c.corner || "").toLowerCase().includes(qq) || (c.dr || "").toLowerCase().includes(qq) || (c.bm || "").toLowerCase().includes(qq));
  }, [corners, q]);

  // Sort — identique au style CA Jour (ChevronsUpDown neutre → ChevronUp asc → ChevronDown desc)
  const statutRank = { "Bon": 3, "Vigilance": 2, "À surveiller": 1 };
  const cornerCmp = useCallback((a, b, k) => {
    switch (k) {
      case "corner":       return (a.corner || "").localeCompare(b.corner || "");
      case "region":       return (a.region || "").localeCompare(b.region || "");
      case "effectif":     return (a.effectif || 0) - (b.effectif || 0);
      case "heures":       return (a.heures || 0) - (b.heures || 0);
      case "hs":           return (a.hs || 0) - (b.hs || 0);
      case "ms":           return (a.ms || 0) - (b.ms || 0);
      case "habs":         return (a.habs || 0) - (b.habs || 0);
      case "coutH":        return (a.coutH || 0) - (b.coutH || 0);
      case "prod":         return (a.prod || 0) - (b.prod || 0);
      case "absenteisme":  return (a.absenteisme || 0) - (b.absenteisme || 0);
      case "statut":       return (statutRank[a.statut] || 0) - (statutRank[b.statut] || 0);
      default:             return 0;
    }
  }, []);
  const { sortKey, sortDir, toggleSort, sorted } = useKpiSort(filtered, "ms", "desc", cornerCmp);

  const total = useMemo(() => {
    const s = corners.reduce((a, c) => ({
      effectif: a.effectif + (c.effectif || 0),
      heures: a.heures + (c.heures || 0),
      hs: a.hs + (c.hs || 0),
      habs: a.habs + (c.habs || 0),
      ms: a.ms + (c.ms || 0),
    }), { effectif: 0, heures: 0, hs: 0, habs: 0, ms: 0 });
    return s;
  }, [corners]);

  const listHeight = Math.min(360, Math.max(80, sorted.length * 36 + 8));

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="rh-detail-corner-table">
      <div className="p-3 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-bold text-slate-900 text-sm">Détail par corner</h2>
          <p className="text-[10px] text-slate-500">{sorted.length} / {corners.length} corners</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"/>
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Rechercher un corner…"
                   data-testid="rh-corner-search"
                   className="pl-8 pr-3 py-1.5 rounded-lg border border-slate-200 text-xs w-56 outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400"/>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-[1.6fr_1fr_0.7fr_1fr_0.9fr_1.2fr_0.9fr_0.9fr_0.9fr_0.9fr_0.9fr] gap-0 border-b-2 border-slate-200 bg-slate-50 px-3 py-2">
        <div><SortHeadCell k="corner"      label="Corner"          align="left"  sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} testid="rh-corner-th-corner"/></div>
        <div><SortHeadCell k="region"      label="Région"          align="left"  sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} testid="rh-corner-th-region"/></div>
        <div><SortHeadCell k="effectif"    label="Effectif"        align="right" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} testid="rh-corner-th-effectif"/></div>
        <div><SortHeadCell k="heures"      label="Heures"          align="right" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} testid="rh-corner-th-heures"/></div>
        <div><SortHeadCell k="hs"          label="H. sup"          align="right" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} testid="rh-corner-th-hs"/></div>
        <div><SortHeadCell k="ms"          label="Masse Salariale" align="right" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} testid="rh-corner-th-ms"/></div>
        <div><SortHeadCell k="habs"        label="H. abs"          align="right" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} testid="rh-corner-th-habs"/></div>
        <div><SortHeadCell k="coutH"       label="Coût Horaire"    align="right" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} testid="rh-corner-th-couth"/></div>
        <div><SortHeadCell k="prod"        label="Productivité"    align="right" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} testid="rh-corner-th-prod"/></div>
        <div><SortHeadCell k="absenteisme" label="Absentéisme"     align="right" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} testid="rh-corner-th-abs"/></div>
        <div><SortHeadCell k="statut"      label="Statut"          align="right" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} testid="rh-corner-th-statut"/></div>
      </div>
      <div style={{ height: listHeight }}>
        <RwList rowCount={sorted.length} rowHeight={36} defaultHeight={listHeight}
                rowComponent={DetailCornerRow} rowProps={{ rows: sorted }}/>
      </div>
      <div className="grid grid-cols-[1.6fr_1fr_0.7fr_1fr_0.9fr_1.2fr_0.9fr_0.9fr_0.9fr_0.9fr_0.9fr] items-center border-t-2 border-slate-300 bg-slate-100 px-3 py-2 text-[11px] font-bold">
        <div className="text-slate-900">Total réseau</div>
        <div className="text-slate-500"></div>
        <div className="text-right tabular-nums">{fmtNum(total.effectif)}</div>
        <div className="text-right tabular-nums">{fmtH(total.heures)}</div>
        <div className="text-right tabular-nums text-amber-700">{fmtH(total.hs)}</div>
        <div className="text-right tabular-nums">{fmtEur(total.ms)}</div>
        <div className="text-right tabular-nums text-rose-600">{fmtH(total.habs)}</div>
        <div/><div/><div/><div/>
      </div>
    </div>
  );
}

/* ─── Sub: Top 5 Card ────────────────────────────────────────────────── */
function Top5Card({ icon: Icon, color, title, items, fmt, valKey, testid }) {
  const clr = { amber: "text-amber-500 bg-amber-100", rose: "text-rose-500 bg-rose-100", emerald: "text-emerald-500 bg-emerald-100", pink: "text-pink-500 bg-pink-100", orange: "text-orange-500 bg-orange-100" }[color] || "text-slate-500 bg-slate-100";
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-3.5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${clr.split(" ")[0]}`}/>
        <h3 className="font-bold text-slate-800 text-xs flex-1">{title}</h3>
      </div>
      <div className="space-y-1">
        {items.map((c, i) => {
          const v = c[valKey];
          const disp = fmt === "eurH" ? fmtEurH(v) : fmt === "pct" ? fmtPct(v) : fmt === "eur" ? fmtEur(v) : fmt === "h" ? fmtH(v) : fmtNum(v);
          return (
            <div key={c.corner} className="flex items-center gap-2 text-[11px] py-0.5">
              <span className={`w-4 h-4 rounded-full ${clr} grid place-items-center font-bold text-[9px] shrink-0`}>{i + 1}</span>
              <span className="flex-1 truncate font-medium text-slate-800">{c.corner}</span>
              <span className="tabular-nums font-bold text-slate-900">{disp}</span>
            </div>
          );
        })}
        {items.length === 0 && <div className="text-[10px] italic text-slate-400 text-center py-1">Aucune donnée</div>}
      </div>
    </div>
  );
}

/* ─── Sub: Collab Detail Table — RH ONLY (view mode segmented) ─────── */
function CollabDetailTable({ rhRows }) {
  const [mode, setMode] = useState("collab"); // global | dr | secteur | poste | collab
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState([]);

  const groups = useMemo(() => {
    if (mode === "global") {
      const a = aggRh(rhRows);
      return [{ label: "Réseau total", effectif: a.effectif, heures: a.h_travaillees, hs: a.hs, habs: a.habs, ms: a.masse_salariale, coutH: a.taux_horaire_moyen, absenteisme: a.absenteisme, count: rhRows.length }];
    }
    const keyFn = mode === "dr" ? (r => r.dr || "—")
                : mode === "secteur" ? (r => r.secteur || "—")
                : mode === "poste" ? (r => r.poste || "—")
                : (r => empKey(r));
    const map = {};
    rhRows.forEach(r => {
      const k = keyFn(r); if (!k) return;
      if (!map[k]) map[k] = { label: k, corner: r.corner, dr: r.dr, secteur: r.secteur, poste: r.poste, rows: [] };
      map[k].rows.push(r);
    });
    return Object.values(map).map(g => {
      const a = aggRh(g.rows);
      return {
        ...g, effectif: a.effectif, heures: a.h_travaillees, hs: a.hs, habs: a.habs, ms: a.masse_salariale,
        coutH: a.taux_horaire_moyen, absenteisme: a.absenteisme, count: g.rows.length,
      };
    }).sort((a, b) => (b.ms || 0) - (a.ms || 0));
  }, [rhRows, mode]);

  const options = useMemo(() => groups.map(g => g.label), [groups]);
  const filtered = useMemo(() => {
    let rows = groups;
    if (selected.length) rows = rows.filter(g => selected.includes(g.label));
    if (q.trim()) {
      const qq = q.trim().toLowerCase();
      rows = rows.filter(g => (g.label || "").toLowerCase().includes(qq) || (g.corner || "").toLowerCase().includes(qq));
    }
    return rows;
  }, [groups, selected, q]);

  // Sort — identique CA Jour (ChevronsUpDown / ChevronUp / ChevronDown)
  const collabCmp = useCallback((a, b, k) => {
    switch (k) {
      case "label":        return (a.label || "").localeCompare(b.label || "");
      case "effectif":     return (a.effectif || 0) - (b.effectif || 0);
      case "heures":       return (a.heures || 0) - (b.heures || 0);
      case "hs":           return (a.hs || 0) - (b.hs || 0);
      case "ms":           return (a.ms || 0) - (b.ms || 0);
      case "habs":         return (a.habs || 0) - (b.habs || 0);
      case "coutH":        return (a.coutH || 0) - (b.coutH || 0);
      case "absenteisme":  return (a.absenteisme || 0) - (b.absenteisme || 0);
      default:             return 0;
    }
  }, []);
  const { sortKey, sortDir, toggleSort, sorted } = useKpiSort(filtered, "ms", "desc", collabCmp);

  const modes = [
    { k: "global",  l: "Global" },
    { k: "dr",      l: "DR" },
    { k: "secteur", l: "Secteur" },
    { k: "poste",   l: "Poste" },
    { k: "collab",  l: "Collaborateur" },
  ];

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="rh-collab-detail-table">
      <div className="p-3 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-bold text-slate-900 text-sm">Détail par collaborateur</h2>
          <p className="text-[10px] text-slate-500">{sorted.length} / {groups.length} entrées</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex bg-slate-100 rounded-lg p-0.5">
            <span className="text-[9px] font-semibold uppercase text-slate-500 px-2 py-1">Vu par :</span>
            {modes.map(m => (
              <button key={m.k} onClick={() => { setMode(m.k); setSelected([]); }}
                      data-testid={`rh-collab-view-${m.k}`}
                      className={`text-[10px] px-2 py-1 rounded-md font-semibold transition-colors ${mode === m.k ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-700"}`}>
                {m.l}
              </button>
            ))}
          </div>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"/>
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Rechercher…"
                   data-testid="rh-collab-search"
                   className="pl-8 pr-3 py-1.5 rounded-lg border border-slate-200 text-xs w-48 outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400"/>
          </div>
          <MultiSelect label="Sélection" testid="rh-collab-multi" options={options.slice(0, 500)} values={selected} onChange={setSelected}/>
        </div>
      </div>
      <div className="grid grid-cols-[2fr_1fr_0.9fr_0.9fr_1.2fr_0.9fr_0.9fr_0.9fr] gap-0 border-b-2 border-slate-200 bg-slate-50 px-3 py-2">
        <div><SortHeadCell k="label"       label={mode === "collab" ? "Collaborateur" : mode === "dr" ? "DR" : mode === "secteur" ? "Secteur" : mode === "poste" ? "Poste" : "Ensemble"}
                                            align="left"  sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} testid="rh-collab-th-label"/></div>
        <div><SortHeadCell k="effectif"    label="Effectif"        align="right" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} testid="rh-collab-th-effectif"/></div>
        <div><SortHeadCell k="heures"      label="Heures"          align="right" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} testid="rh-collab-th-heures"/></div>
        <div><SortHeadCell k="hs"          label="H. supp"         align="right" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} testid="rh-collab-th-hs"/></div>
        <div><SortHeadCell k="ms"          label="Masse salariale" align="right" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} testid="rh-collab-th-ms"/></div>
        <div><SortHeadCell k="habs"        label="H. abs"          align="right" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} testid="rh-collab-th-habs"/></div>
        <div><SortHeadCell k="coutH"       label="Coût h"          align="right" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} testid="rh-collab-th-couth"/></div>
        <div><SortHeadCell k="absenteisme" label="Absentéisme"     align="right" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} testid="rh-collab-th-abs"/></div>
      </div>
      <div className="max-h-[420px] overflow-y-auto">
        {sorted.slice(0, 500).map((g, i) => (
          <div key={g.label + i} className="grid grid-cols-[2fr_1fr_0.9fr_0.9fr_1.2fr_0.9fr_0.9fr_0.9fr] items-center border-b border-slate-100 hover:bg-slate-50/70 px-3 py-1.5 text-[11px]">
            <div className="flex items-center gap-2 min-w-0">
              <span className="w-1.5 h-1.5 rounded-full bg-teal-500 shrink-0"/>
              <span className="font-semibold text-slate-900 truncate">{g.label}</span>
              {mode === "collab" && g.poste && <span className="text-[9px] text-slate-500 truncate">· {g.poste}</span>}
            </div>
            <div className="text-right tabular-nums font-semibold">{fmtNum(g.effectif)}</div>
            <div className="text-right tabular-nums">{fmtH(g.heures)}</div>
            <div className={`text-right tabular-nums ${g.hs > 0 ? "text-amber-700 font-semibold" : "text-slate-500"}`}>{fmtH(g.hs || 0)}</div>
            <div className="text-right tabular-nums font-semibold">{fmtEur(g.ms)}</div>
            <div className={`text-right tabular-nums ${g.habs > 0 ? "text-rose-600 font-semibold" : "text-slate-500"}`}>{fmtH(g.habs || 0)}</div>
            <div className="text-right tabular-nums">{fmtEurH(g.coutH)}</div>
            <div className={`text-right tabular-nums ${g.absenteisme > 0.05 ? "text-rose-600 font-semibold" : "text-slate-700"}`}>{fmtPct(g.absenteisme)}</div>
          </div>
        ))}
        {filtered.length === 0 && <div className="p-6 text-center text-xs italic text-slate-400">Aucune donnée</div>}
      </div>
    </div>
  );
}

/* ─── Productivité tab (own FilterBar + own /kpi/productivite fetch) ─ */
function ProductiviteTab() {
  const [filters, setFilters] = useState({ drs: [], secteurs: [], bms: [], corners: [], mois: [], semaines: [], exercices: [] });
  const [f, setF] = useSharedKpiFilters({ drs: [], secteurs: [], corners: [], exercice: null, mois: [], semaines: [], annees: [] }, { meta: filters, pageKey: "orbit_rh_prod_filters" });
  const [prodData, setProdData] = useState(null);
  useEffect(() => {
    api.get("/kpi/productivite/meta").then(r => setFilters(r.data || {})).catch(() => {});
  }, []);
  useEffect(() => {
    const params = toParams({ drs: f.drs, secteurs: f.secteurs, corners: f.corners, semaines: f.semaines, mois: f.mois, annees: f.annees });
    if (f.exercice) params.exercice = f.exercice;
    api.get("/kpi/productivite", { params }).then(r => setProdData(r.data || null)).catch(() => setProdData(null));
  }, [f.drs, f.secteurs, f.corners, f.exercice, f.mois, f.semaines, f.annees]);
  const exercicesFromData = filters.exercices || [];
  return (
    <div className="space-y-4">
      <FilterBar filters={filters} f={f} setF={setF} exercicesFromData={exercicesFromData} showAnnee/>
      <ProductiviteContent prodData={prodData} filters={f}/>
    </div>
  );
}

/* ─── Productivité content (was ProductiviteTab) ─────────────────── */
function ProductiviteContent({ prodData, filters }) {
  const [weekSel, setWeekSel] = useState(null);
  const summary = prodData?.summary || {};
  const kpis = useMemo(() => {
    const ca = summary.ca_ht || 0;
    const heures = summary.heures || 0;
    const prod = summary.productivite_globale || (heures ? ca / heures : 0);
    const gold = summary.gold_avg || 60;
    const ecart = prod - gold;
    const goldPct = gold ? prod / gold : 0;
    // ─── Agrégations corner-par-corner basées sur la cible individuelle ───
    // Lecture heures : "Heures théoriques" = CA / cible_corner
    //   → écart_h = heures_réelles − heures_théoriques (signe conservé)
    // Lecture CA    : "CA théorique" = heures_réelles × cible_corner
    //   → CA manquant = max(0, CA_théo − CA_réel) — pas de compensation
    let totEcartH = 0, totCaManquant = 0;
    let hOptim = 0, hReserveCasse = 0, hAutres = 0;
    for (const c of (prodData?.by_corner || [])) {
      const goldC = c.gold_standard || 0;
      if (!goldC) continue;
      const hth = (c.ca_ht || 0) / goldC;
      const hag = (c.heure || 0) - hth;
      totEcartH += hag;
      const caTheo = (c.heure || 0) * goldC;
      totCaManquant += Math.max(0, caTheo - (c.ca_ht || 0));
      // Ventilation par diagnostic (aligné backend — source de vérité)
      const key = c.diagnostic_key;
      if (key === "C_operationnel_insuffisant")           hOptim += hag;
      else if (key === "B_prod_ok_casse_a_corriger")      hReserveCasse += hag;
      else                                                hAutres += hag;
    }
    const hagLabel = totEcartH >= 0 ? "Heures à gagner" : "Besoin d'heures";
    const hagValue = (totEcartH >= 0 ? "+" : "−") + fmtH(Math.abs(totEcartH));
    // Sub-text : détail des 3 catégories (backend renvoie les mêmes chiffres dans summary)
    const bkOptim = prodData?.summary?.heures_reellement_optimisables ?? hOptim;
    const bkCasse = prodData?.summary?.heures_sous_reserve_casse ?? hReserveCasse;
    const hagSub = `Optim: ${fmtH(bkOptim)} · Sous réserve casse: ${fmtH(bkCasse)}`;
    return [
      { key: "ca", icon: Users, iconColor: "text-blue-600", iconBg: "bg-blue-50",
        label: "CA HT total", value: fmtEur(ca), delta: null },
      { key: "heures", icon: Clock, iconColor: "text-cyan-600", iconBg: "bg-cyan-50",
        label: "Heures travaillées", value: fmtH(heures), delta: null },
      { key: "prod", icon: Euro, iconColor: "text-emerald-600", iconBg: "bg-emerald-50",
        label: "Productivité globale", value: fmtEurH(prod), delta: null },
      { key: "gold", icon: Target, iconColor: "text-yellow-600", iconBg: "bg-yellow-50",
        label: "Gold Standard (moy.)", value: fmtEurH(gold),
        sub: `Écart vs Gold : ${ecart >= 0 ? "+" : ""}${fmtEurH(ecart)}`, delta: null },
      { key: "ca_manquant", icon: AlertTriangle,
        iconColor: totCaManquant > 0 ? "text-rose-600" : "text-emerald-600",
        iconBg:    totCaManquant > 0 ? "bg-rose-50"    : "bg-emerald-50",
        label: "CA manquant", value: fmtEur(totCaManquant),
        sub: "vs cible de productivité", delta: null },
      { key: "hag", icon: Clock,
        iconColor: totEcartH >= 0 ? "text-amber-600" : "text-rose-600",
        iconBg:    totEcartH >= 0 ? "bg-amber-50"    : "bg-rose-50",
        label: hagLabel, value: hagValue, sub: hagSub, delta: null },
      { key: "obj", icon: Trophy, iconColor: "text-violet-600", iconBg: "bg-violet-50",
        label: "Objectif atteint", value: fmtPct(goldPct), progress: goldPct, delta: null },
      { key: "corners", icon: LayoutGrid, iconColor: "text-slate-600", iconBg: "bg-slate-100",
        label: "Corners actifs", value: fmtNum(summary.n_corners), delta: null },
    ];
  }, [summary, prodData]);

  // Evolution prod chart — support toggle Mois/Semaine
  const [prodMode, setProdMode] = useState("mois"); // mois | semaine
  // Cible Gold Standard dynamique : moyenne pondérée par heures des cibles
  // (cible_productivite / gold_standard) des corners du filtre actif — fallback
  // sur la moyenne renvoyée par le backend (cards). Aucun défaut en dur à 60.
  const goldTargetAvg = useMemo(() => {
    const corners = prodData?.by_corner || [];
    let num = 0, den = 0, sum = 0, n = 0;
    for (const c of corners) {
      const g = c.cible_productivite ?? c.gold_standard;
      if (g == null || isNaN(g) || g <= 0) continue;
      const h = c.heure || 0;
      if (h > 0) { num += g * h; den += h; }
      sum += g; n += 1;
    }
    if (den > 0) return num / den;
    if (n > 0)   return sum / n;
    const fallback = prodData?.summary?.gold_avg;
    return fallback && !isNaN(fallback) ? fallback : 0;
  }, [prodData]);
  const evoData = useMemo(() => {
    const target = Math.round((goldTargetAvg || 0) * 10) / 10;
    if (prodMode === "semaine") {
      const rows = (prodData?.weekly || []).slice()
        .sort((a,b) => (a.semaine||0) - (b.semaine||0));
      return rows.map(w => ({
        mois: w.label, // S01, S02…
        semaine: w.semaine,
        "Productivité": w.productivite ? Math.round(w.productivite * 10) / 10 : 0,
        "Gold Standard": target,
        date_start: w.date_start, date_end: w.date_end,
      }));
    }
    return (prodData?.monthly || []).map(m => ({
      mois: MONTH_ABBR[m.mois] || m.mois,
      "Productivité": m.productivite ? Math.round(m.productivite * 10) / 10 : 0,
      "Gold Standard": target,
    }));
  }, [prodData, prodMode, goldTargetAvg]);

  const distribution = useMemo(() => {
    const cs = (prodData?.by_corner || []).filter(c => c.productivite > 0);
    const B = [["lt45", "< 45 €/h", "bg-rose-500", v => v < 45], ["45_55", "45–55", "bg-orange-400", v => v >= 45 && v < 55], ["55_60", "55–60", "bg-yellow-400", v => v >= 55 && v < 60], ["60_70", "60–70", "bg-emerald-400", v => v >= 60 && v < 70], ["gt70", "> 70 €/h", "bg-emerald-600", v => v >= 70]];
    const G = [["tres_sous", "🔴 Très sous cible (< 80 %)", "bg-rose-500", p => p < 0.8], ["sous", "🟠 Sous cible (80–90 %)", "bg-orange-400", p => p >= 0.8 && p < 0.9], ["proche", "🟡 Proche cible (90–100 %)", "bg-yellow-400", p => p >= 0.9 && p < 1], ["atteint", "🟢 Objectif atteint (≥ 100 %)", "bg-emerald-500", p => p >= 1]];
    return { total: cs.length,
      buckets: B.map(([k, label, bg, fn]) => ({ k, label, bg, n: cs.filter(c => fn(c.productivite)).length })),
      vsGold: G.map(([k, label, bg, fn]) => ({ k, label, bg, n: cs.filter(c => fn(c.gold_standard ? c.productivite / c.gold_standard : 0)).length })) };
  }, [prodData]);

  const matrix = useMemo(() => {
    const cs = (prodData?.by_corner || []).filter(c => c.productivite > 0 && c.taux_casse != null);
    const golds = cs.map(c => c.gold_standard).filter(Boolean);
    const goldRef = golds.length ? Math.round(golds.reduce((a, b) => a + b, 0) / golds.length * 10) / 10 : 60;
    const Z = { model: { k: "model", label: "Modèle", bg: "bg-emerald-500", hex: "#10B981", pill: "bg-emerald-50 text-emerald-700", text: "text-emerald-700" }, watch: { k: "watch", label: "Surveiller la casse", bg: "bg-amber-500", hex: "#F59E0B", pill: "bg-amber-50 text-amber-700", text: "text-amber-700" }, optimize: { k: "optimize", label: "Optimiser organisation", bg: "bg-yellow-400", hex: "#EAB308", pill: "bg-yellow-50 text-yellow-700", text: "text-yellow-700" }, priority: { k: "priority", label: "Priorité", bg: "bg-rose-500", hex: "#EF4444", pill: "bg-rose-50 text-rose-700", text: "text-rose-700" } };
    const points = cs.map(c => { const hi = c.productivite >= (c.gold_standard || goldRef), lowC = c.taux_casse <= CASSE_THRESHOLDS.target; return { corner: c.corner, x: Math.round(c.productivite * 10) / 10, y: Math.round(c.taux_casse * 1000) / 10, z: c.ca_ht || 0, zone: hi ? (lowC ? Z.model : Z.watch) : (lowC ? Z.optimize : Z.priority) }; });
    return { goldRef, points, zones: Object.values(Z).map(z => ({ ...z, n: points.filter(p => p.zone.k === z.k).length })) };
  }, [prodData]);

  // Corner detail — PROD ONLY, aucune donnée de kpi_rh
  const cornerDetail = useMemo(() => (prodData?.by_corner || []).map(c => {
    const ca_ht = c.ca_ht || 0;
    const heure = c.heure || 0;
    const gold_standard = c.gold_standard || 60;
    // Valeurs pré-calculées côté backend (source unique de vérité)
    const heures_theoriques_cible = c.heures_theoriques ?? (gold_standard ? ca_ht / gold_standard : 0);
    const ecart_h_cible = c.heures_a_gagner ?? (heure - heures_theoriques_cible);
    const ca_theorique_cible = c.ca_cible ?? (heure * gold_standard);
    const ca_manquant = c.ca_manquant ?? Math.max(0, ca_theorique_cible - ca_ht);
    const production_reelle = c.has_prod_data ? (c.production_reelle || 0) : null;
    const casse_eur = c.has_prod_data ? (c.casse_eur || 0) : null;
    const productivite_production = c.productivite_production ?? null;
    const ecart_prod_ca = c.ecart_prod_ca ?? null;   // usage interne — plus affiché en colonne
    const taux_casse = c.taux_casse ?? null;
    // Diagnostic : préférer les valeurs backend (source de vérité)
    const diagnostic = c.diagnostic_label ? {
      key: c.diagnostic_key, label: c.diagnostic_label, tone: c.diagnostic_tone,
      reco_priorite_casse: !!c.reco_priorite_casse,
    } : computeDiagnostic({
      prod_com: c.productivite || 0,
      prod_production: productivite_production,
      cible: gold_standard,
      taux_casse,
    });
    return {
      corner: c.corner, dr: c.dr, secteur: c.secteur, bm: c.bm,
      ca_ht, heure,
      productivite: c.productivite || 0,
      gold_standard,
      ecart_gs: c.ecart_gs || 0,
      pct_atteinte: c.pct_atteinte || 0,
      heures_cibles: c.heures_cibles || 0,
      surplus_besoin: c.surplus_besoin || 0,
      heures_theoriques_cible, ecart_h_cible, ca_theorique_cible, ca_manquant,
      production_reelle, casse_eur, productivite_production, ecart_prod_ca, taux_casse,
      niveau_casse: c.niveau_casse || "na",
      has_prod_data: !!c.has_prod_data,
      diagnostic,
      source: c.productivite_source || "excel",
      gold_source: c.gold_source || "default",
    };
  }).sort((a, b) => b.productivite - a.productivite), [prodData]);

  /* ── View mode : globale | corner | secteur | dr ── */
  const [view, setView] = useState("corner");
  const [status, setStatus] = useState("all"); // all | avance | objectif | retard
  const [q, setQ] = useState("");
  const [sortKey, setSortKey] = useState("productivite");
  const [sortDir, setSortDir] = useState("desc");
  const toggleSort = (k) => {
    if (sortKey === k) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortKey(k);
      setSortDir("desc");
    }
  };

  /* Aggregation helper: regroupe par une clé (dr / secteur / global) et calcule prod pondérée */
  const aggregate = (rows, keyFn, labelFn) => {
    const groups = new Map();
    for (const r of rows) {
      const k = keyFn(r) || "—";
      if (!groups.has(k)) groups.set(k, { key: k, label: labelFn ? labelFn(r) : k, corners: 0, ca_ht: 0, heure: 0, gold_weighted_num: 0, gold_weighted_den: 0, surplus_besoin: 0, production_reelle: 0, casse_eur: 0, has_prod_data_count: 0 });
      const g = groups.get(k);
      g.corners += 1;
      g.ca_ht += r.ca_ht || 0;
      g.heure += r.heure || 0;
      g.gold_weighted_num += (r.gold_standard || 60) * (r.heure || 0);
      g.gold_weighted_den += r.heure || 0;
      g.surplus_besoin += r.surplus_besoin || 0;
      if (r.has_prod_data) {
        g.production_reelle += r.production_reelle || 0;
        g.casse_eur += r.casse_eur || 0;
        g.has_prod_data_count += 1;
      }
    }
    return Array.from(groups.values()).map(g => {
      const prod = g.heure ? g.ca_ht / g.heure : 0;
      const gold = g.gold_weighted_den ? g.gold_weighted_num / g.gold_weighted_den : 60;
      const heures_theoriques_cible = gold ? g.ca_ht / gold : 0;
      const ecart_h_cible = g.heure - heures_theoriques_cible;
      const ca_theorique_cible = g.heure * gold;
      const ca_manquant = Math.max(0, ca_theorique_cible - g.ca_ht);
      const has_prod = g.has_prod_data_count > 0;
      const production_reelle = has_prod ? g.production_reelle : null;
      const casse_eur = has_prod ? g.casse_eur : null;
      const productivite_production = (has_prod && g.heure) ? g.production_reelle / g.heure : null;
      const impact_perte = (productivite_production != null) ? productivite_production - prod : null;
      const taux_casse = (has_prod && g.production_reelle) ? g.casse_eur / g.production_reelle : null;
      const diagnostic = computeDiagnostic({ prod_com: prod, prod_production: productivite_production, cible: gold, taux_casse });
      return {
        key: g.key, label: g.label, corners: g.corners,
        ca_ht: g.ca_ht, heure: g.heure,
        productivite: prod, gold_standard: gold,
        ecart_gs: prod - gold,
        pct_atteinte: gold ? prod / gold : 0,
        surplus_besoin: g.surplus_besoin,
        heures_theoriques_cible, ecart_h_cible, ca_theorique_cible, ca_manquant,
        production_reelle, casse_eur, productivite_production, impact_perte, taux_casse,
        has_prod_data: has_prod, diagnostic,
      };
    }).sort((a, b) => b.pct_atteinte - a.pct_atteinte);
  };

  /* Rows to render based on selected view */
  const viewRows = useMemo(() => {
    if (view === "corner") return cornerDetail.map(c => ({ ...c, label: c.corner }));
    if (view === "secteur") return aggregate(cornerDetail, r => r.secteur, r => r.secteur);
    if (view === "dr")      return aggregate(cornerDetail, r => r.dr, r => r.dr);
    /* globale */
    return aggregate(cornerDetail, () => "Réseau global", () => "Réseau global");
  }, [cornerDetail, view]);

  /* Apply search + status filter + sort */
  const filteredRows = useMemo(() => {
    const qq = q.trim().toLowerCase();
    const base = viewRows.filter(r => {
      // search
      if (qq) {
        const hay = `${r.label || r.corner || ""} ${r.dr || ""} ${r.secteur || ""}`.toLowerCase();
        if (!hay.includes(qq)) return false;
      }
      // status
      const p = r.pct_atteinte || 0;
      if (status === "avance"   && !(p >= 1)) return false;
      if (status === "objectif" && !(p >= 0.9 && p < 1)) return false;
      if (status === "retard"   && !(p < 0.9)) return false;
      return true;
    });
    const dir = sortDir === "asc" ? 1 : -1;
    const getVal = (r, k) => {
      switch (k) {
        case "label":            return (r.label || r.corner || "").toLowerCase();
        case "dr":               return (r.dr || "").toLowerCase();
        case "secteur":          return (r.secteur || "").toLowerCase();
        case "ca_ht":            return r.ca_ht || 0;
        case "heure":            return r.heure || 0;
        case "productivite":     return r.productivite || 0;
        case "productivite_production": return r.productivite_production != null ? r.productivite_production : -Infinity;
        case "gold_standard":    return r.gold_standard || 0;
        case "ecart_gs":         return r.ecart_gs || 0;
        case "ecart_h_cible":    return r.ecart_h_cible || 0;
        case "ca_manquant":      return r.ca_manquant || 0;
        case "taux_casse":       return r.taux_casse != null ? r.taux_casse : Infinity;
        case "diagnostic":       return (r.diagnostic?.label || "").toLowerCase();
        default: return 0;
      }
    };
    return [...base].sort((a, b) => {
      const va = getVal(a, sortKey);
      const vb = getVal(b, sortKey);
      if (typeof va === "string") return va.localeCompare(vb) * dir;
      return (va - vb) * dir;
    });
  }, [viewRows, q, status, sortKey, sortDir]);

  /* Total row (over filtered rows to reflect current selection) */
  const totalRow = useMemo(() => {
    const t = filteredRows.reduce((acc, r) => {
      acc.ca_ht += r.ca_ht || 0;
      acc.heure += r.heure || 0;
      acc.gold_weighted_num += (r.gold_standard || 60) * (r.heure || 0);
      acc.gold_weighted_den += r.heure || 0;
      acc.surplus_besoin += r.surplus_besoin || 0;
      acc.ecart_h_cible += r.ecart_h_cible || 0;
      acc.ca_manquant += r.ca_manquant || 0;
      if (r.has_prod_data) {
        acc.production_reelle += r.production_reelle || 0;
        acc.casse_eur += r.casse_eur || 0;
        acc.has_prod_count += 1;
      }
      return acc;
    }, { ca_ht: 0, heure: 0, gold_weighted_num: 0, gold_weighted_den: 0, surplus_besoin: 0, ecart_h_cible: 0, ca_manquant: 0, production_reelle: 0, casse_eur: 0, has_prod_count: 0 });
    const prod = t.heure ? t.ca_ht / t.heure : 0;
    const gold = t.gold_weighted_den ? t.gold_weighted_num / t.gold_weighted_den : 60;
    const has_prod = t.has_prod_count > 0;
    const productivite_production = (has_prod && t.heure) ? t.production_reelle / t.heure : null;
    const taux_casse = (has_prod && t.production_reelle) ? t.casse_eur / t.production_reelle : null;
    return {
      count: filteredRows.length,
      ca_ht: t.ca_ht, heure: t.heure,
      productivite: prod, gold_standard: gold,
      ecart_gs: prod - gold, pct_atteinte: gold ? prod / gold : 0,
      surplus_besoin: t.surplus_besoin,
      ecart_h_cible: t.ecart_h_cible, ca_manquant: t.ca_manquant,
      production_reelle: has_prod ? t.production_reelle : null,
      casse_eur: has_prod ? t.casse_eur : null,
      productivite_production, taux_casse,
      impact_perte: productivite_production != null ? productivite_production - prod : null,
      has_prod_data: has_prod,
      diagnostic: computeDiagnostic({ prod_com: prod, prod_production: productivite_production, cible: gold, taux_casse }),
    };
  }, [filteredRows]);

  /* Status counts (for badge numbers) — computed over viewRows (pre search) */
  const statusCounts = useMemo(() => {
    let a = 0, o = 0, r = 0;
    for (const row of viewRows) {
      const p = row.pct_atteinte || 0;
      if (p >= 1) a++; else if (p >= 0.9) o++; else r++;
    }
    return { all: viewRows.length, avance: a, objectif: o, retard: r };
  }, [viewRows]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-8 gap-2" data-testid="prod-kpi-row">
        {kpis.map(c => <KpiCard key={c.key} {...c} testid={`prod-kpi-${c.key}`}/>)}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4 lg:col-span-2" data-testid="prod-evolution-chart">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <h3 className="font-bold text-slate-800 text-sm">Évolution de la productivité (CA / heure)
              {prodMode === "semaine" && <span className="ml-2 text-[10px] font-normal text-blue-600">— cliquez une semaine pour le détail jour par jour</span>}
            </h3>
            <div className="inline-flex items-center border border-slate-200 rounded overflow-hidden" data-testid="prod-evol-toggle">
              <button data-testid="prod-evol-mois" onClick={()=>setProdMode("mois")}
                      className={`px-2.5 py-1 text-[11px] font-semibold ${prodMode==="mois"?"bg-blue-600 text-white":"bg-white text-slate-600 hover:bg-slate-50"}`}>Mois</button>
              <button data-testid="prod-evol-semaine" onClick={()=>setProdMode("semaine")}
                      className={`px-2.5 py-1 text-[11px] font-semibold ${prodMode==="semaine"?"bg-blue-600 text-white":"bg-white text-slate-600 hover:bg-slate-50"}`}>Semaine</button>
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer>
              <ComposedChart data={evoData} margin={{ top: 6, right: 12, left: 0, bottom: 0 }}
                             onClick={(st) => { const p = st?.activePayload?.[0]?.payload; if (prodMode === "semaine" && p?.semaine) setWeekSel(p.semaine); }}
                             style={{ cursor: prodMode === "semaine" ? "pointer" : "default" }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9"/>
                <XAxis dataKey="mois" tick={{ fontSize: 10, fill: "#64748B" }} axisLine={false} tickLine={false}/>
                <YAxis tick={{ fontSize: 10, fill: "#64748B" }} axisLine={false} tickLine={false} width={40}/>
                <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 6, fontSize: 12 }}/>
                <Legend wrapperStyle={{ fontSize: 11 }}/>
                <Bar dataKey="Productivité" fill="#2563EB" radius={[4, 4, 0, 0]} barSize={22} className={prodMode === "semaine" ? "cursor-pointer" : ""}
                     onClick={(d) => { if (prodMode === "semaine" && d?.semaine) setWeekSel(d.semaine); }}/>
                <Line type="monotone" dataKey="Gold Standard" stroke="#16A34A" strokeDasharray="5 3" strokeWidth={2} dot={false} legendType="line"/>
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          {weekSel && <WeekDrillDrawer semaine={weekSel} filters={filters} onClose={() => setWeekSel(null)}/>}
        </div>
        <div className="space-y-3">
          <div className="bg-white rounded-xl border border-slate-200 p-3" data-testid="prod-distribution">
            <h3 className="font-bold text-slate-800 text-xs mb-1 flex items-center gap-1.5">
              <LayoutGrid className="w-3.5 h-3.5 text-blue-600"/> Distribution du réseau
            </h3>
            <p className="text-[10px] text-slate-500 mb-2">Où se situe le réseau ? · {distribution.total} corners</p>
            <div className="flex h-3 rounded-full overflow-hidden bg-slate-100 mb-2">
              {distribution.buckets.map(b => b.n > 0 && <div key={b.k} className={b.bg} style={{ width: `${b.n / Math.max(1, distribution.total) * 100}%` }} title={`${b.label} : ${b.n}`}/>)}
            </div>
            <div className="grid grid-cols-5 gap-1 mb-3">
              {distribution.buckets.map(b => (
                <div key={b.k} className="text-center" data-testid={`prod-bucket-${b.k}`}>
                  <div className={`w-2 h-2 rounded-full mx-auto mb-0.5 ${b.bg}`}/>
                  <div className="text-[11px] font-bold tabular-nums text-slate-900">{b.n}</div>
                  <div className="text-[8px] text-slate-500 leading-tight">{b.label}</div>
                </div>
              ))}
            </div>
            <div className="text-[9px] font-bold uppercase tracking-wider text-slate-400 mb-1">Répartition vs Gold</div>
            <div className="space-y-1">
              {distribution.vsGold.map(g => (
                <div key={g.k} className="flex items-center gap-2 text-[11px]" data-testid={`prod-vsgold-${g.k}`}>
                  <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${g.bg}`}/>
                  <span className="flex-1 text-slate-700">{g.label}</span>
                  <div className="w-20 h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className={`h-full ${g.bg}`} style={{ width: `${g.n / Math.max(1, distribution.total) * 100}%` }}/></div>
                  <span className="tabular-nums font-bold text-slate-900 w-7 text-right">{g.n}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-3" data-testid="prod-matrix">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
          <div>
            <h3 className="font-bold text-slate-800 text-xs flex items-center gap-1.5"><Target className="w-3.5 h-3.5 text-violet-600"/> Matrice Productivité × Casse</h3>
            <p className="text-[10px] text-slate-500">Chaque point = un corner · Seuils : Gold moyen {fmtEurH(matrix.goldRef)} · Casse cible {fmtPct(CASSE_THRESHOLDS.target)}</p>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {matrix.zones.map(z => (
              <span key={z.k} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${z.pill}`} data-testid={`prod-zone-${z.k}`}>
                <span className={`w-2 h-2 rounded-full ${z.bg}`}/>{z.label} <b>{z.n}</b>
              </span>
            ))}
          </div>
        </div>
        <div className="h-[300px]">
          <ResponsiveContainer>
            <ScatterChart margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
              <XAxis type="number" dataKey="x" name="Productivité" unit=" €/h" tick={{ fontSize: 10 }} domain={["auto", "auto"]} label={{ value: "Productivité €/h →", position: "insideBottomRight", offset: -2, fontSize: 10, fill: "#64748b" }}/>
              <YAxis type="number" dataKey="y" name="Casse" unit=" %" tick={{ fontSize: 10 }} width={44} domain={[0, "auto"]} label={{ value: "Casse % ↑", angle: -90, position: "insideLeft", fontSize: 10, fill: "#64748b" }}/>
              <ZAxis type="number" dataKey="z" range={[30, 260]} name="CA HT"/>
              <ReferenceLine x={matrix.goldRef} stroke="#16A34A" strokeDasharray="5 3" label={{ value: "Gold", fontSize: 9, fill: "#16A34A", position: "top" }}/>
              <ReferenceLine y={CASSE_THRESHOLDS.target * 100} stroke="#F59E0B" strokeDasharray="5 3" label={{ value: "Cible casse", fontSize: 9, fill: "#D97706", position: "right" }}/>
              <Tooltip cursor={{ strokeDasharray: "3 3" }} content={({ payload }) => {
                const p = payload?.[0]?.payload; if (!p) return null;
                return <div className="bg-white border border-slate-200 rounded-lg shadow-lg px-3 py-2 text-[11px]"><div className="font-bold text-slate-900">{p.corner}</div><div>Productivité : <b>{fmtEurH(p.x)}</b></div><div>Casse : <b>{p.y.toFixed(1)} %</b></div><div>CA HT : {fmtEur(p.z)}</div><div className={`mt-1 font-semibold ${p.zone.text}`}>{p.zone.label}</div></div>;
              }}/>
              {matrix.zones.map(z => <Scatter key={z.k} name={z.label} data={matrix.points.filter(p => p.zone.k === z.k)} fill={z.hex} fillOpacity={0.75}/>)}
            </ScatterChart>
          </ResponsiveContainer>
        </div>
        <div className="grid grid-cols-2 gap-1 mt-2 text-[10px]">
          <div className="rounded-lg bg-amber-50 text-amber-800 px-2 py-1">🟠 Productivité élevée · Casse élevée → <b>Surveiller la casse</b></div>
          <div className="rounded-lg bg-emerald-50 text-emerald-800 px-2 py-1">🟢 Productivité élevée · Casse faible → <b>Modèle</b></div>
          <div className="rounded-lg bg-rose-50 text-rose-800 px-2 py-1">🔴 Productivité faible · Casse élevée → <b>Priorité</b></div>
          <div className="rounded-lg bg-yellow-50 text-yellow-800 px-2 py-1">🟡 Productivité faible · Casse faible → <b>Optimiser l'organisation</b></div>
        </div>
      </div>

      {/* Détail par corner — PROD ONLY (colonnes du fichier importé) */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="prod-corner-detail-table">
        <div className="p-3 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="font-bold text-slate-900 text-sm">Détail productivité — {view === "corner" ? "par corner" : view === "secteur" ? "par secteur" : view === "dr" ? "par DR" : "réseau global"}</h2>
            <p className="text-[10px] text-slate-500">{filteredRows.length} ligne(s) affichée(s) · Source : kpi_prod · Objectif Gold : productivite_targets (défaut 60)</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* View pills */}
            <div className="flex items-center gap-1 bg-slate-100 rounded-full p-1" data-testid="prod-view-switcher">
              {[
                { k: "globale", label: "Vue Globale" },
                { k: "corner",  label: "Vue Corner"  },
                { k: "secteur", label: "Vue Secteur" },
                { k: "dr",      label: "Vue DR"      },
              ].map(v => (
                <button key={v.k} onClick={() => setView(v.k)}
                        data-testid={`prod-view-${v.k}`}
                        className={`px-3 py-1 rounded-full text-[11px] font-semibold transition-colors ${view === v.k ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>
                  {v.label}
                </button>
              ))}
            </div>
            {/* Search */}
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"/>
              <input value={q} onChange={e => setQ(e.target.value)} placeholder="Rechercher…"
                     data-testid="prod-corner-search"
                     className="pl-8 pr-3 py-1.5 rounded-full border border-slate-200 text-xs w-48 outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400"/>
            </div>
            {/* Status pills */}
            <div className="flex items-center gap-1 bg-slate-100 rounded-full p-1" data-testid="prod-status-switcher">
              {[
                { k: "all",      label: "Tous",           count: statusCounts.all,      color: "text-slate-900" },
                { k: "avance",   label: "En avance",      count: statusCounts.avance,   color: "text-emerald-700" },
                { k: "objectif", label: "Dans l'objectif",count: statusCounts.objectif, color: "text-amber-700"   },
                { k: "retard",   label: "En retard",      count: statusCounts.retard,   color: "text-rose-700"    },
              ].map(s => (
                <button key={s.k} onClick={() => setStatus(s.k)}
                        data-testid={`prod-status-${s.k}`}
                        className={`px-3 py-1 rounded-full text-[11px] font-semibold transition-colors flex items-center gap-1.5 ${status === s.k ? "bg-white shadow-sm " + s.color : "text-slate-500 hover:text-slate-800"}`}>
                  {s.label}
                  <span className={`text-[9px] font-bold px-1.5 rounded-full ${status === s.k ? "bg-slate-100 text-slate-700" : "bg-slate-200 text-slate-500"}`}>{s.count}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-[1.5fr_0.75fr_0.75fr_0.9fr_0.75fr_1.05fr_1.05fr_0.7fr_0.8fr_0.85fr_0.95fr_0.7fr_1.4fr] border-b-2 border-slate-200 bg-slate-50 px-3 py-2">
          <div><SortHeadCell k="label" label={view === "corner" ? "Corner" : view === "secteur" ? "Secteur" : view === "dr" ? "DR" : "Réseau"} sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} testid="prod-th-label"/></div>
          <div><SortHeadCell k="dr" label={view === "corner" ? "DR" : "Corners"} sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} testid="prod-th-dr"/></div>
          <div><SortHeadCell k="secteur" label={view === "corner" ? "Secteur" : "—"} sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} testid="prod-th-secteur"/></div>
          <div className="text-right"><SortHeadCell k="ca_ht" label="CA HT" align="right" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} testid="prod-th-ca"/></div>
          <div className="text-right"><SortHeadCell k="heure" label="Heures" align="right" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} testid="prod-th-heure"/></div>
          <div className="text-right"><SortHeadCell k="productivite" label="Productivité commerciale" title="Productivité commerciale = CA HT ÷ Heures travaillées (€/h). Mesure le chiffre d'affaires généré par heure de travail." align="right" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} testid="prod-th-prod"/></div>
          <div className="text-right"><SortHeadCell k="productivite_production" label="Productivité production" title="Productivité production = Production réelle (valeur produite, import Achat Hebdo) ÷ Heures travaillées (€/h). Mesure la valeur fabriquée par heure, indépendamment des ventes." align="right" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} testid="prod-th-prodprod"/></div>
          <div className="text-right"><SortHeadCell k="gold_standard" label="Gold" title="Objectif Gold Standard de productivité (€/h) défini par corner dans productivite_targets (défaut 60 €/h)." align="right" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} testid="prod-th-gold"/></div>
          <div className="text-right"><SortHeadCell k="ecart_gs" label="Écart Gold" title="Écart au Gold = Productivité commerciale − Gold Standard (€/h). Positif = objectif dépassé." align="right" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} testid="prod-th-ecartgold"/></div>
          <div className="text-right"><SortHeadCell k="ecart_h_cible" label="H. à gagner" align="right" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} testid="prod-th-ecart"/></div>
          <div className="text-right"><SortHeadCell k="ca_manquant" label="CA manq." align="right" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} testid="prod-th-manquant"/></div>
          <div className="text-right"><SortHeadCell k="taux_casse" label="Casse %" align="right" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} testid="prod-th-casse"/></div>
          <div><SortHeadCell k="diagnostic" label="Diagnostic" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} testid="prod-th-diag"/></div>
        </div>
        <div className="max-h-[420px] overflow-y-auto">
          {filteredRows.slice(0, 500).map((c, i) => {
            const p = c.pct_atteinte || 0;
            const statusPillCls = p >= 1 ? "bg-emerald-500" : p >= 0.9 ? "bg-amber-500" : "bg-rose-500";
            return (
              <div key={(c.corner || c.key || c.label) + i} className="grid grid-cols-[1.5fr_0.75fr_0.75fr_0.9fr_0.75fr_1.05fr_1.05fr_0.7fr_0.8fr_0.85fr_0.95fr_0.7fr_1.4fr] items-center border-b border-slate-100 hover:bg-slate-50/70 px-3 py-1.5 text-[11px]">
                <div className="flex items-center gap-2 min-w-0">
                  <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${statusPillCls}`}/>
                  <span className="font-semibold text-slate-900 truncate">{c.label || c.corner}</span>
                  {view === "corner" && c.source === "computed" && <span className="text-[8px] px-1 rounded bg-amber-100 text-amber-700 font-semibold">calc</span>}
                  {view === "corner" && c.gold_source === "default" && <span className="text-[8px] px-1 rounded bg-slate-100 text-slate-500 font-semibold" title="Aucune target — défaut 60 €/h">def</span>}
                </div>
                <div className="text-slate-600 truncate">{view === "corner" ? (c.dr || "—") : `${c.corners || 0} corner${(c.corners || 0) > 1 ? "s" : ""}`}</div>
                <div className="text-slate-600 truncate">{view === "corner" ? (c.secteur || "—") : "—"}</div>
                <div className="text-right tabular-nums font-semibold">{fmtEur(c.ca_ht)}</div>
                <div className="text-right tabular-nums">{fmtH(c.heure)}</div>
                <div className="text-right tabular-nums font-bold text-blue-700">{fmtEurH(c.productivite)}</div>
                <div className={`text-right tabular-nums font-semibold ${c.productivite_production == null ? "text-slate-300 italic" : "text-violet-700"}`}
                     title={c.productivite_production == null ? "Aucune correspondance corner+période dans import Achat Hebdo" : `Production ${fmtEur(c.production_reelle)} / ${fmtH(c.heure)}`}>
                  {c.productivite_production == null ? "N/A" : fmtEurH(c.productivite_production)}
                </div>
                <div className="text-right tabular-nums text-slate-600">{fmtEurH(c.gold_standard)}</div>
                <div className={`text-right tabular-nums font-semibold ${(c.ecart_gs||0) >= 0 ? "text-emerald-600" : "text-rose-600"}`} data-testid="prod-cell-ecartgold">
                  {(c.ecart_gs||0) >= 0 ? "+" : ""}{fmtEurH(c.ecart_gs || 0)}
                </div>
                <div className={`text-right tabular-nums font-semibold ${(c.ecart_h_cible||0) > 0 ? "text-amber-700" : (c.ecart_h_cible||0) < 0 ? "text-rose-600" : "text-slate-500"}`}>
                  {fmtHSigned(c.ecart_h_cible)}
                </div>
                <div className={`text-right tabular-nums font-semibold ${(c.ca_manquant||0) > 0 ? "text-rose-600" : "text-slate-400"}`}>
                  {fmtEur(c.ca_manquant || 0)}
                </div>
                <div className={`text-right tabular-nums font-semibold ${
                     c.taux_casse == null ? "text-slate-300" :
                     c.taux_casse > CASSE_THRESHOLDS.alert    ? "text-rose-700"   :   /* > 12 % */
                     c.taux_casse > CASSE_THRESHOLDS.target   ? "text-amber-700"  :   /* > 10 % ≤ 12 % */
                     c.taux_casse > CASSE_THRESHOLDS.tres_bon ? "text-emerald-700":   /* > 8 %  ≤ 10 % */
                                                                "text-emerald-600"    /* ≤ 8 % */
                   }`}
                   title={
                     c.niveau_casse === "alerte"       ? "Alerte casse (> 12 %)" :
                     c.niveau_casse === "a_surveiller" ? "À surveiller (> 10 % · au-dessus cible entreprise)" :
                     c.niveau_casse === "maitrise"     ? "Maîtrisé (8 % → 10 %)" :
                     c.niveau_casse === "tres_bon"     ? "Très bon (≤ 8 %)" : "N/A"
                   }>
                  {c.taux_casse == null ? "—" : fmtPct(c.taux_casse)}
                </div>
                <div>
                  <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-semibold ${
                    c.diagnostic?.tone === "emerald" ? "bg-emerald-100 text-emerald-700" :
                    c.diagnostic?.tone === "amber" ? "bg-amber-100 text-amber-700" :
                    c.diagnostic?.tone === "rose" ? "bg-rose-100 text-rose-700" :
                    c.diagnostic?.tone === "orange" ? "bg-orange-100 text-orange-700" :
                    "bg-slate-100 text-slate-500"
                  }`} title={c.diagnostic?.label}>
                    {c.diagnostic?.label || "—"}
                  </span>
                </div>
              </div>
            );
          })}
          {filteredRows.length === 0 && <div className="p-6 text-center text-xs italic text-slate-400">Aucune donnée — importe le fichier Productivité et/ou ajuste les filtres.</div>}
        </div>
        {/* Total row (sticky bottom) */}
        {filteredRows.length > 0 && (
          <div className="grid grid-cols-[1.5fr_0.75fr_0.75fr_0.9fr_0.75fr_1.05fr_1.05fr_0.7fr_0.8fr_0.85fr_0.95fr_0.7fr_1.4fr] items-center border-t-2 border-slate-300 bg-slate-100 px-3 py-2 text-[11px] font-bold text-slate-900" data-testid="prod-total-row">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-slate-900"/>
              <span>TOTAL ({totalRow.count})</span>
            </div>
            <div className="text-slate-600 truncate">—</div>
            <div className="text-slate-600 truncate">—</div>
            <div className="text-right tabular-nums">{fmtEur(totalRow.ca_ht)}</div>
            <div className="text-right tabular-nums">{fmtH(totalRow.heure)}</div>
            <div className="text-right tabular-nums text-blue-700">{fmtEurH(totalRow.productivite)}</div>
            <div className={`text-right tabular-nums ${totalRow.productivite_production == null ? "text-slate-300" : "text-violet-700"}`}>{totalRow.productivite_production == null ? "N/A" : fmtEurH(totalRow.productivite_production)}</div>
            <div className="text-right tabular-nums text-slate-700">{fmtEurH(totalRow.gold_standard)}</div>
            <div className={`text-right tabular-nums ${((totalRow.productivite||0) - (totalRow.gold_standard||0)) >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{((totalRow.productivite||0) - (totalRow.gold_standard||0)) >= 0 ? "+" : ""}{fmtEurH((totalRow.productivite||0) - (totalRow.gold_standard||0))}</div>
            <div className={`text-right tabular-nums ${(totalRow.ecart_h_cible||0) > 0 ? "text-amber-700" : (totalRow.ecart_h_cible||0) < 0 ? "text-rose-600" : "text-slate-500"}`}>{fmtHSigned(totalRow.ecart_h_cible)}</div>
            <div className={`text-right tabular-nums ${(totalRow.ca_manquant||0) > 0 ? "text-rose-600" : "text-slate-400"}`}>{fmtEur(totalRow.ca_manquant || 0)}</div>
            <div className={`text-right tabular-nums ${
                 totalRow.taux_casse == null ? "text-slate-300" :
                 totalRow.taux_casse > CASSE_THRESHOLDS.alert    ? "text-rose-700"   :
                 totalRow.taux_casse > CASSE_THRESHOLDS.target   ? "text-amber-700"  :
                 totalRow.taux_casse > CASSE_THRESHOLDS.tres_bon ? "text-emerald-700":
                                                                   "text-emerald-600"
               }`}>{totalRow.taux_casse == null ? "—" : fmtPct(totalRow.taux_casse)}</div>
            <div>
              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-semibold ${
                totalRow.diagnostic?.tone === "emerald" ? "bg-emerald-100 text-emerald-700" :
                totalRow.diagnostic?.tone === "amber" ? "bg-amber-100 text-amber-700" :
                totalRow.diagnostic?.tone === "rose" ? "bg-rose-100 text-rose-700" :
                totalRow.diagnostic?.tone === "orange" ? "bg-orange-100 text-orange-700" :
                "bg-slate-100 text-slate-500"
              }`}>{totalRow.diagnostic?.label || "—"}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


/* ─── Simulateur tab ─────────────────────────────────────────────────── */
function SimulateurTab({ rhRows, prodData, monthAgg }) {
  const [ca, setCa] = useState(1220000);
  const [gold, setGold] = useState(60);
  const [hs, setHs] = useState(120);
  const [prime, setPrime] = useState(2000);
  const [tauxCasse, setTauxCasse] = useState(1.2);
  const [absent, setAbsent] = useState(4.0);
  const [includeHs, setIncludeHs] = useState(true);
  const [newEmployee, setNewEmployee] = useState({ contrat: "CDD", poste: "Chef Sushi", heures: 35, salaire: 1850 });

  const actual = monthAgg.cur;
  const actualCa = prodData?.summary?.ca_ht || 1122000;
  const actualHeures = actual?.h_travaillees || 21120;
  const actualMs = actual?.masse_salariale || 220000;
  const actualProd = actualHeures ? actualCa / actualHeures : 54.8;
  const actualTauxMs = actualCa ? actualMs / actualCa : 0.196;

  // Simulation calc
  const sim = useMemo(() => {
    const heuresNouv = includeHs ? actualHeures + hs : actualHeures;
    const heuresAvecNew = heuresNouv + (newEmployee.heures * 4.33);
    const prodEst = heuresAvecNew ? ca / heuresAvecNew : 0;
    const msNouv = actualMs + (hs * 20) + prime + newEmployee.salaire * 1.4;
    const contribution = ca * 0.20 - msNouv * 0.1;
    const tauxMs = ca ? msNouv / ca : 0;
    const seuilRent = newEmployee.salaire * 1.4 / 0.03; // approximate breakeven CA
    return {
      heures_prevues: heuresAvecNew,
      prod_est: prodEst,
      gold_atteint: gold ? prodEst / gold : 0,
      taux_ms: tauxMs,
      ms_totale: msNouv,
      resultat: ca - msNouv - ca * (tauxCasse / 100),
      contribution,
      cout_hs: hs * 25.5,
      seuil_rent: seuilRent,
      impact_prod: prodEst - actualProd,
      impact_ms: (tauxMs - actualTauxMs) * 100,
    };
  }, [ca, gold, hs, prime, tauxCasse, absent, includeHs, newEmployee, actualHeures, actualMs, actualProd, actualTauxMs]);

  // Evolution scenarios chart
  const scenarios = useMemo(() => {
    const pts = [-20, -10, 0, 10, 20].map(pct => {
      const c = actualCa * (1 + pct / 100);
      const h = actualHeures;
      const p = h ? c / h : 0;
      const m = actualMs;
      const mn = c ? m / c : 0;
      return { label: `${pct > 0 ? "+" : ""}${pct}% CA`, "Productivité (€/h)": Math.round(p * 10) / 10, "Masse salariale (%)": Math.round(mn * 1000) / 10, "Contribution (€)": Math.round(c * 0.15) };
    });
    return pts;
  }, [actualCa, actualHeures, actualMs]);

  const kpis = [
    { key: "ca", icon: TrendingUp, iconColor: "text-blue-600", iconBg: "bg-blue-50", label: "CA prévu", value: fmtEur(ca), delta: ((ca - actualCa) / actualCa) * 100, unit: "%" },
    { key: "h", icon: Clock, iconColor: "text-cyan-600", iconBg: "bg-cyan-50", label: "Heures prévues", value: fmtH(sim.heures_prevues), delta: ((sim.heures_prevues - actualHeures) / actualHeures) * 100, unit: "%" },
    { key: "prod", icon: Euro, iconColor: "text-emerald-600", iconBg: "bg-emerald-50", label: "Productivité estimée", value: fmtEurH(sim.prod_est), delta: sim.impact_prod, unit: "" },
    { key: "gold", icon: Target, iconColor: "text-yellow-600", iconBg: "bg-yellow-50", label: "Gold Standard", value: fmtEurH(gold), progress: sim.gold_atteint, delta: null },
    { key: "ms", icon: TrendingUp, iconColor: "text-violet-600", iconBg: "bg-violet-50", label: "Masse salariale %", value: fmtPct(sim.taux_ms), delta: sim.impact_ms, unit: "pt" },
    { key: "cost", icon: Euro, iconColor: "text-orange-600", iconBg: "bg-orange-50", label: "Coût salarial", value: fmtEur(sim.ms_totale), delta: ((sim.ms_totale - actualMs) / actualMs) * 100, unit: "%" },
    { key: "res", icon: TrendingUp, iconColor: "text-emerald-600", iconBg: "bg-emerald-50", label: "Résultat estimé", value: fmtEur(sim.resultat > 0 ? sim.resultat / 30 : 0), delta: 18.4, unit: "%" },
    { key: "contrib", icon: Euro, iconColor: "text-green-600", iconBg: "bg-green-50", label: "Contribution", value: fmtEur(sim.contribution), delta: 12.8, unit: "%" },
  ];

  const reset = () => {
    setCa(1220000); setGold(60); setHs(120); setPrime(2000); setTauxCasse(1.2); setAbsent(4.0); setIncludeHs(true);
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-8 gap-2" data-testid="simu-kpi-row">
        {kpis.map(c => <KpiCard key={c.key} {...c} testid={`simu-kpi-${c.key}`}/>)}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* 1. Hypothèses */}
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="simu-hypotheses">
          <h3 className="font-bold text-slate-900 text-sm mb-1">1. Hypothèses de simulation</h3>
          <p className="text-[10px] text-slate-500 mb-3">Ajustez les paramètres pour voir l'impact en temps réel</p>
          <div className="space-y-3">
            <SliderRow label="CA prévu" value={ca} min={800000} max={2000000} step={10000} unit="€" onChange={setCa} display={fmtEur(ca)} deltaPct={((ca - actualCa) / actualCa) * 100}/>
            <SliderRow label="Gold Standard (€/h)" value={gold} min={45} max={75} step={0.5} unit="€/h" onChange={setGold} display={`${gold} €/h`}/>
            <div>
              <SliderRow label="Heures supplémentaires" value={hs} min={0} max={300} step={5} unit="h" onChange={setHs} display={`${hs} h`}/>
              <label className="flex items-center gap-1 text-[10px] text-slate-500 mt-1 ml-2">
                <input type="checkbox" checked={includeHs} onChange={e => setIncludeHs(e.target.checked)} className="w-3 h-3"/>
                Inclure HS
              </label>
            </div>
            <SliderRow label="Prime exceptionnelle" value={prime} min={0} max={10000} step={100} unit="€" onChange={setPrime} display={fmtEur(prime)}/>
            <SliderRow label="Taux casse estimé" value={tauxCasse} min={0} max={5} step={0.1} unit="%" onChange={setTauxCasse} display={`${tauxCasse.toFixed(1).replace(".", ",")}%`}/>
            <SliderRow label="Absentéisme estimé" value={absent} min={0} max={10} step={0.1} unit="%" onChange={setAbsent} display={`${absent.toFixed(1).replace(".", ",")}%`}/>
          </div>
          <div className="flex items-center gap-2 mt-4">
            <button onClick={reset} data-testid="simu-reset" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-slate-700 text-[11px] font-semibold hover:bg-slate-50">
              <RotateCcw className="w-3.5 h-3.5"/> Réinitialiser
            </button>
            <button onClick={() => toast.success("Scénario appliqué")} data-testid="simu-apply" className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 text-white text-[11px] font-bold hover:bg-blue-700">
              <Play className="w-3.5 h-3.5"/> Appliquer le scénario
            </button>
          </div>
        </div>

        {/* 2. Simulateur d'embauche */}
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="simu-embauche">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-bold text-slate-900 text-sm">2. Simulateur d'embauche</h3>
            <button className="flex items-center gap-1 text-[11px] text-blue-600 font-semibold hover:underline">
              <UserPlus className="w-3.5 h-3.5"/> Ajouter un salarié
            </button>
          </div>
          <p className="text-[10px] text-slate-500 mb-3">Testez l'impact d'un nouveau collaborateur</p>
          <div className="bg-slate-50 rounded-lg p-3 space-y-2">
            <div className="flex items-center gap-1.5 text-[11px] font-bold text-slate-700 mb-1">
              <UserPlus className="w-3.5 h-3.5 text-blue-600"/> Nouvel employé #1
            </div>
            <div className="grid grid-cols-2 gap-2 text-[11px]">
              <div>
                <label className="text-[9px] font-semibold text-slate-500 uppercase mb-0.5 block">Type de contrat</label>
                <SearchSelect value={newEmployee.contrat} onChange={v => setNewEmployee(o => ({ ...o, contrat: v }))} options={[{ value: "CDI", label: "CDI" }, { value: "CDD", label: "CDD" }, { value: "APPRENTI", label: "APPRENTI" }, { value: "ALTERNANT", label: "ALTERNANT" }, { value: "EXTRA", label: "EXTRA" }]} size="lg" menuWidth={260}/>
              </div>
              <div>
                <label className="text-[9px] font-semibold text-slate-500 uppercase mb-0.5 block">Poste</label>
                <input value={newEmployee.poste} onChange={e => setNewEmployee(o => ({ ...o, poste: e.target.value }))} className="w-full px-2 py-1 rounded border border-slate-300 text-[11px]"/>
              </div>
              <div>
                <label className="text-[9px] font-semibold text-slate-500 uppercase mb-0.5 block">Heures / semaine</label>
                <input type="number" value={newEmployee.heures} onChange={e => setNewEmployee(o => ({ ...o, heures: +e.target.value }))} className="w-full px-2 py-1 rounded border border-slate-300 text-[11px]"/>
              </div>
              <div>
                <label className="text-[9px] font-semibold text-slate-500 uppercase mb-0.5 block">Salaire brut mensuel</label>
                <input type="number" value={newEmployee.salaire} onChange={e => setNewEmployee(o => ({ ...o, salaire: +e.target.value }))} className="w-full px-2 py-1 rounded border border-slate-300 text-[11px]"/>
              </div>
            </div>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2 text-center">
            <div className="bg-blue-50 rounded-lg p-2">
              <div className="text-[8px] font-bold uppercase text-blue-700">Productivité</div>
              <div className="text-[14px] font-bold text-blue-900 tabular-nums">{fmtEurH(sim.prod_est)}</div>
              <div className={`text-[9px] font-semibold ${sim.impact_prod >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{sim.impact_prod >= 0 ? "↗" : "↘"} {sim.impact_prod.toFixed(1).replace(".", ",")}</div>
            </div>
            <div className="bg-violet-50 rounded-lg p-2">
              <div className="text-[8px] font-bold uppercase text-violet-700">Masse salariale</div>
              <div className="text-[14px] font-bold text-violet-900 tabular-nums">{sim.impact_ms >= 0 ? "+" : ""}{sim.impact_ms.toFixed(1).replace(".", ",")} pt</div>
              <div className={`text-[9px] font-semibold ${sim.impact_ms <= 0 ? "text-emerald-600" : "text-rose-600"}`}>{sim.impact_ms <= 0 ? "↘" : "↗"} {Math.abs(sim.impact_ms).toFixed(1).replace(".", ",")}%</div>
            </div>
            <div className="bg-emerald-50 rounded-lg p-2">
              <div className="text-[8px] font-bold uppercase text-emerald-700">Contribution</div>
              <div className="text-[14px] font-bold text-emerald-900 tabular-nums">{fmtEurShort(sim.contribution)}</div>
              <div className={`text-[9px] font-semibold ${sim.contribution >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{sim.contribution >= 0 ? "↗" : "↘"} 19,7%</div>
            </div>
          </div>
          <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <Target className="w-4 h-4 text-emerald-600 mt-0.5"/>
              <div className="flex-1 text-[11px]">
                <div className="font-bold text-emerald-800">Seuil de rentabilité</div>
                <div className="text-emerald-700">Cette embauche devient rentable à partir de <span className="font-bold">{fmtEur(sim.seuil_rent)}</span> de CA (+7,9%).</div>
              </div>
            </div>
          </div>
        </div>

        {/* 3. Simulation HS */}
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="simu-hs">
          <h3 className="font-bold text-slate-900 text-sm mb-1">3. Simulation heures supplémentaires</h3>
          <p className="text-[10px] text-slate-500 mb-3">Comparez le coût des heures sup. vs une embauche</p>
          <div className="mb-3">
            <label className="text-[10px] font-semibold text-slate-500 uppercase mb-1 block">Nombre d'heures sup.</label>
            <input type="range" min={0} max={300} step={5} value={hs} onChange={e => setHs(+e.target.value)}
                   className="w-full h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-600"/>
            <div className="flex justify-between text-[9px] text-slate-500 mt-0.5">
              <span>0</span><span>150</span><span className="font-bold text-blue-700">{hs} h</span><span>250</span><span>300</span>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 text-center mb-3">
            <div>
              <div className="text-[9px] font-bold uppercase text-slate-500">Coût des HS</div>
              <div className="text-[14px] font-bold text-slate-900 tabular-nums">{fmtEur(sim.cout_hs)}</div>
              <div className="text-[9px] text-slate-400">+25% (taux majoré)</div>
            </div>
            <div className="border-x border-slate-200">
              <div className="text-[9px] font-bold uppercase text-slate-500">Équivalent</div>
              <div className="text-[14px] font-bold text-slate-900 tabular-nums">{(sim.cout_hs / (newEmployee.salaire * 1.4)).toFixed(2)} salarié</div>
              <div className="text-[9px] text-slate-400">(sur un mois)</div>
            </div>
            <div>
              <div className="text-[9px] font-bold uppercase text-slate-500">Alternative</div>
              <div className="text-[14px] font-bold text-slate-900 tabular-nums">CDD 1 mois</div>
              <div className="text-[9px] text-slate-400">{fmtEur(newEmployee.salaire * 1.4)}</div>
            </div>
          </div>
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <OrbitAI className="w-4 h-4 text-purple-600 mt-0.5"/>
              <div className="flex-1 text-[11px]">
                <div className="font-bold text-purple-800 mb-0.5">Recommandation IA</div>
                <div className="inline-block bg-emerald-100 text-emerald-800 rounded px-2 py-0.5 text-[10px] font-bold mb-1">Embauche recommandée</div>
                <div className="text-slate-700">À partir de 80h supplémentaires, le coût dépasse celui d'un CDD. Un CDD est plus rentable dès 5 semaines et apporte plus de flexibilité.</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Row 2: Evolution + Comparatif + Calculateur */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="simu-evolution">
          <h3 className="font-bold text-slate-900 text-sm mb-2">4. Évolution selon vos scénarios</h3>
          <div className="h-72">
            <ResponsiveContainer>
              <LineChart data={scenarios} margin={{ top: 6, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9"/>
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#64748B" }}/>
                <YAxis yAxisId="left" tick={{ fontSize: 10, fill: "#64748B" }} label={{ value: "€/h", angle: -90, position: "insideLeft", fontSize: 9 }}/>
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: "#64748B" }}/>
                <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 6, fontSize: 11 }}/>
                <Legend wrapperStyle={{ fontSize: 10 }} iconType="line"/>
                <Line yAxisId="left" type="monotone" dataKey="Productivité (€/h)" stroke={CLR.blue} strokeWidth={2.5} dot={{ r: 3 }}/>
                <Line yAxisId="left" type="monotone" dataKey="Masse salariale (%)" stroke={CLR.green} strokeWidth={2.5} dot={{ r: 3 }}/>
                <Line yAxisId="right" type="monotone" dataKey="Contribution (€)" stroke={CLR.orange} strokeWidth={2.5} dot={{ r: 3 }}/>
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="simu-comparatif">
          <h3 className="font-bold text-slate-900 text-sm mb-2">5. Comparatif détaillé</h3>
          <table className="w-full text-[11px]">
            <thead className="border-b border-slate-200">
              <tr className="text-slate-500 text-[9px] uppercase">
                <th className="text-left py-1.5">Indicateur</th>
                <th className="text-right py-1.5">Actuel</th>
                <th className="text-right py-1.5">Simulation</th>
                <th className="text-right py-1.5">Écart</th>
                <th className="text-right py-1.5">%</th>
              </tr>
            </thead>
            <tbody>
              {[
                { l: "CA HT", a: fmtEur(actualCa), s: fmtEur(ca), e: fmtEurShort(ca - actualCa), p: `+${((ca - actualCa) / actualCa * 100).toFixed(1).replace(".", ",")}%`, tone: "up" },
                { l: "Heures travaillées", a: fmtH(actualHeures), s: fmtH(sim.heures_prevues), e: `+${fmtH(sim.heures_prevues - actualHeures)}`, p: `+${((sim.heures_prevues - actualHeures) / actualHeures * 100).toFixed(1).replace(".", ",")}%`, tone: "up" },
                { l: "Productivité", a: fmtEurH(actualProd), s: fmtEurH(sim.prod_est), e: `${sim.impact_prod >= 0 ? "+" : ""}${sim.impact_prod.toFixed(1).replace(".", ",")}`, p: `${sim.impact_prod >= 0 ? "+" : ""}${(sim.impact_prod / actualProd * 100).toFixed(1).replace(".", ",")}%`, tone: sim.impact_prod >= 0 ? "up" : "down" },
                { l: "Masse salariale", a: fmtPct(actualTauxMs), s: fmtPct(sim.taux_ms), e: `${sim.impact_ms >= 0 ? "+" : ""}${sim.impact_ms.toFixed(1).replace(".", ",")} pt`, p: `${sim.impact_ms >= 0 ? "+" : ""}${(sim.impact_ms / (actualTauxMs * 100) * 100).toFixed(1).replace(".", ",")}%`, tone: sim.impact_ms <= 0 ? "up" : "down" },
                { l: "Coût horaire", a: fmtEurH(7.73), s: fmtEurH(7.71), e: "-0,02 €", p: "-0,3%", tone: "up" },
                { l: "Contribution", a: fmtEurShort(182000), s: fmtEurShort(sim.contribution), e: fmtEurShort(sim.contribution - 182000), p: "+12,8%", tone: "up" },
                { l: "Gold Standard", a: fmtEurH(60), s: fmtEurH(gold), e: `${gold - 60 >= 0 ? "+" : ""}${(gold - 60).toFixed(1).replace(".", ",")}`, p: `${((gold - 60) / 60 * 100).toFixed(1).replace(".", ",")}%`, tone: gold >= 60 ? "up" : "down" },
              ].map(r => (
                <tr key={r.l} className="border-b border-slate-100">
                  <td className="py-1.5 text-slate-700">{r.l}</td>
                  <td className="py-1.5 text-right tabular-nums text-slate-600">{r.a}</td>
                  <td className="py-1.5 text-right tabular-nums font-semibold text-slate-900">{r.s}</td>
                  <td className={`py-1.5 text-right tabular-nums font-semibold ${r.tone === "up" ? "text-emerald-600" : "text-rose-600"}`}>{r.e}</td>
                  <td className={`py-1.5 text-right tabular-nums font-bold ${r.tone === "up" ? "text-emerald-600" : "text-rose-600"}`}>{r.p}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="simu-calculateur">
          <h3 className="font-bold text-slate-900 text-sm mb-1">6. Calculateur objectif</h3>
          <p className="text-[10px] text-slate-500 mb-3">Quel CA ou combien d'heures pour atteindre votre objectif ?</p>
          <div className="flex bg-slate-100 rounded-lg p-0.5 mb-3">
            <button className="flex-1 text-[11px] py-1 rounded-md font-semibold bg-white shadow-sm text-slate-900">Objectif de productivité</button>
            <button className="flex-1 text-[11px] py-1 rounded-md font-semibold text-slate-500">Objectif de CA</button>
          </div>
          <div className="mb-3">
            <label className="text-[10px] font-semibold text-slate-500 uppercase mb-0.5 block">Je veux atteindre</label>
            <div className="flex items-center gap-2">
              <input type="number" value={gold} onChange={e => setGold(+e.target.value)}
                     className="flex-1 px-2 py-2 rounded border border-slate-300 text-[16px] font-bold text-slate-900 tabular-nums"/>
              <span className="text-[11px] font-semibold text-slate-500">€/h</span>
            </div>
            <div className="text-[9px] text-slate-400 mt-0.5">(Gold Standard)</div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-rose-50 rounded-lg p-3 text-center">
              <RotateCcw className="w-4 h-4 text-rose-600 mx-auto mb-1"/>
              <div className="text-[9px] font-bold uppercase text-rose-800">Ou bien</div>
              <div className="text-[18px] font-bold text-rose-900 tabular-nums my-1">- {fmtH(Math.max(0, actualHeures - ca / gold))}</div>
              <div className="text-[9px] text-rose-700">(réduire les heures)</div>
            </div>
            <div className="bg-blue-50 rounded-lg p-3 text-center">
              <Users className="w-4 h-4 text-blue-600 mx-auto mb-1"/>
              <div className="text-[9px] font-bold uppercase text-blue-800">Il vous faudra</div>
              <div className="text-[18px] font-bold text-blue-900 tabular-nums my-1">{fmtEurShort(gold * actualHeures)}</div>
              <div className="text-[9px] text-blue-700">de CA (+{((gold * actualHeures - actualCa) / actualCa * 100).toFixed(1).replace(".", ",")}% vs actuel)</div>
            </div>
          </div>
          <div className="text-center mt-3 p-2 bg-slate-50 rounded">
            <div className="text-[9px] font-bold uppercase text-slate-600">= <span className="text-slate-900 text-[13px] tabular-nums">- 2,1 salariés</span></div>
            <div className="text-[8px] text-slate-500">(à effectif constant)</div>
          </div>
        </div>
      </div>

      {/* Analyse IA synthèse */}
      <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl border border-purple-200 p-4" data-testid="simu-ia-synthesis">
        <div className="flex items-center gap-2 mb-3">
          <OrbitAI className="w-4 h-4 text-purple-600"/>
          <h3 className="font-bold text-slate-800 text-sm">Analyse IA — Synthèse du scénario</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-[11px]">
          <div className="bg-white/70 rounded-lg p-3">
            <div className="flex items-center gap-1.5 mb-1"><span className="text-emerald-600 text-lg">✓</span><span className="font-bold text-slate-800">Votre simulation améliore la productivité de +{Math.abs(sim.impact_prod).toFixed(1).replace(".", ",")} €/h</span></div>
            <div className="text-slate-600 text-[10px]">Bonne dynamique, vous vous rapprochez du Gold Standard.</div>
          </div>
          <div className="bg-white/70 rounded-lg p-3">
            <div className="flex items-center gap-1.5 mb-1"><span className="text-emerald-600 text-lg">✓</span><span className="font-bold text-slate-800">La masse salariale baisse à {fmtPct(sim.taux_ms)}</span></div>
            <div className="text-slate-600 text-[10px]">{sim.impact_ms.toFixed(1).replace(".", ",")} pt par rapport à la situation actuelle.</div>
          </div>
          <div className="bg-white/70 rounded-lg p-3">
            <div className="flex items-center gap-1.5 mb-1"><span className="text-amber-600 text-lg">⚠</span><span className="font-bold text-slate-800">L'embauche prévue reste rentable</span></div>
            <div className="text-slate-600 text-[10px]">à partir de {fmtEurShort(sim.seuil_rent)} de CA. Vous êtes à +7,9% du seuil.</div>
          </div>
          <div className="bg-white/70 rounded-lg p-3">
            <div className="flex items-center gap-1.5 mb-1"><span className="text-blue-600 text-lg">ℹ</span><span className="font-bold text-slate-800">Limiter les heures sup. au-delà de 80h</span></div>
            <div className="text-slate-600 text-[10px]">Un CDD est plus avantageux financièrement et opérationnellement.</div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Sub: Slider Row ────────────────────────────────────────────────── */
function SliderRow({ label, value, min, max, step, onChange, display, deltaPct }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-[11px] font-medium text-slate-700">{label}</label>
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] font-bold text-slate-900 tabular-nums bg-slate-100 px-2 py-0.5 rounded">{display}</span>
          {deltaPct != null && Math.abs(deltaPct) > 0.1 && (
            <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${deltaPct > 0 ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
              {deltaPct > 0 ? "+" : ""}{deltaPct.toFixed(1).replace(".", ",")}%
            </span>
          )}
        </div>
      </div>
      <input type="range" min={min} max={max} step={step} value={value} onChange={e => onChange(+e.target.value)}
             className="w-full h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-600"/>
    </div>
  );
}

/* ─── Salaires tab ───────────────────────────────────────────────────── */
function SalairesTab({ rhRows, prodData, monthAgg, weekAgg, chargesSalaires }) {
  const cur = monthAgg.cur;
  const prev = monthAgg.prev;
  const kpis = useMemo(() => {
    if (!cur) return [];
    const p = prev || {};
    const chargesFactor = 1.27; // employeur factor
    const coutEmp = cur.masse_salariale * chargesFactor;
    const salaireBrutMoyen = cur.effectif ? cur.salaire_brut / Math.max(1, cur.rows_count || cur.effectif) : 0;
    const ca = prodData?.summary?.ca_ht || 0;
    const tauxMs = ca ? cur.masse_salariale / ca : 0;
    const prevTauxMs = p.masse_salariale && p.ca ? p.masse_salariale / p.ca : null;
    return [
      { key: "ms-brute", icon: Users, iconColor: "text-blue-600", iconBg: "bg-blue-50", label: "Masse salariale brute", value: fmtEur(cur.masse_salariale), delta: p.masse_salariale ? ((cur.masse_salariale - p.masse_salariale) / p.masse_salariale) * 100 : null, unit: "%" },
      { key: "cout-emp", icon: Euro, iconColor: "text-emerald-600", iconBg: "bg-emerald-50", label: "Coût employeur total", value: fmtEur(coutEmp), delta: 4.1, unit: "%" },
      { key: "salaire-moy", icon: Award, iconColor: "text-teal-600", iconBg: "bg-teal-50", label: "Salaire brut moyen", value: fmtEur(1938), delta: 2.1, unit: "%" },
      { key: "cout-h", icon: Clock, iconColor: "text-cyan-600", iconBg: "bg-cyan-50", label: "Coût horaire moyen", value: fmtEurH(cur.taux_horaire_moyen), delta: -1.8, unit: "%" },
      { key: "taux-ms", icon: TrendingUp, iconColor: "text-violet-600", iconBg: "bg-violet-50", label: "Taux de masse salariale", value: fmtPct(tauxMs), delta: prevTauxMs != null ? (tauxMs - prevTauxMs) * 100 : null, unit: "pt" },
      { key: "eff", icon: Users, iconColor: "text-orange-600", iconBg: "bg-orange-50", label: "Effectif rémunéré", value: fmtNum(cur.effectif), delta: 12, unit: "" },
    ];
  }, [cur, prev, prodData]);

  // Evolution monthly/weekly (MS + taux MS) — toggle Mois/Semaine
  const [salMode, setSalMode] = useState("mois");
  const evoData = useMemo(() => {
    if (salMode === "semaine") {
      return (weekAgg || []).map(w => ({
        mois: w.semaine,
        "Masse salariale brute": Math.round(w.masse_salariale || 0),
        "Taux MS (%)": w.ca ? Math.round((w.masse_salariale / w.ca) * 100) : 0,
      }));
    }
    return monthAgg.list.map(m => ({
      mois: MONTH_ABBR[m.mois] || m.mois,
      "Masse salariale brute": Math.round(m.masse_salariale),
      "Taux MS (%)": m.ca ? Math.round((m.masse_salariale / m.ca) * 100) : 0,
    }));
  }, [monthAgg, weekAgg, salMode]);

  // Répartition MS par contrat
  const [repartMode, setRepartMode] = useState("contrat"); // contrat | poste | region
  const repartition = useMemo(() => {
    const map = {};
    rhRows.forEach(r => {
      const k = repartMode === "contrat" ? classifyContrat(r.poste) : repartMode === "poste" ? (r.poste || "—") : (r.region || "—");
      map[k] = (map[k] || 0) + (r.masse_salariale || 0);
    });
    return Object.entries(map).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [rhRows, repartMode]);
  const totalMs = repartition.reduce((s, x) => s + x.value, 0);

  // Coût employeur par poste
  const coutParPoste = useMemo(() => {
    const map = {};
    rhRows.forEach(r => {
      const p = r.poste || "—";
      map[p] = map[p] || { poste: p, ms: 0, n: 0 };
      map[p].ms += r.masse_salariale || 0;
      map[p].n += 1;
    });
    return Object.values(map).map(x => ({ ...x, avg: x.ms * 1.27 / Math.max(1, x.n / 5) })).sort((a, b) => b.avg - a.avg).slice(0, 8);
  }, [rhRows]);
  const maxCout = Math.max(...coutParPoste.map(c => c.avg), 1);

  // Détail collaborateurs (aggregated by name)
  const collabs = useMemo(() => {
    const map = {};
    rhRows.forEach(r => {
      const k = r.name; if (!k) return;
      if (!map[k]) map[k] = { name: r.name, corner: r.corner, poste: r.poste, contrat: classifyContrat(r.poste), rows: [] };
      map[k].rows.push(r);
    });
    return Object.values(map).map(g => {
      const a = aggRh(g.rows);
      const heuresMois = a.h_travaillees / Math.max(1, new Set(g.rows.map(r => r.mois)).size);
      const salaireBrut = g.rows.reduce((s, r) => s + (r.salaire_brut || 0), 0) / Math.max(1, g.rows.length);
      const charges = salaireBrut * 0.4;
      const coutEmp = salaireBrut + charges;
      const etp = g.rows.reduce((s, r) => s + (r.etp_semaine || 0), 0) / Math.max(1, g.rows.length);
      return {
        name: g.name, corner: g.corner, poste: g.poste, contrat: g.contrat,
        heures_mois: heuresMois, cout_h: a.taux_horaire_moyen, salaire_brut: salaireBrut,
        charges, cout_emp: coutEmp, etp, anciennete: Math.round(g.rows.length / 12 * 10) / 10,
        statut: "Actif",
      };
    }).sort((a, b) => (b.cout_emp || 0) - (a.cout_emp || 0));
  }, [rhRows]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2" data-testid="sal-kpi-row">
        {kpis.map(c => <KpiCard key={c.key} {...c} testid={`sal-kpi-${c.key}`}/>)}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="sal-evolution">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <h3 className="font-bold text-slate-800 text-sm">Évolution de la masse salariale</h3>
            <div className="inline-flex items-center border border-slate-200 rounded overflow-hidden" data-testid="sal-evol-toggle">
              <button data-testid="sal-evol-mois" onClick={()=>setSalMode("mois")}
                      className={`px-2.5 py-1 text-[11px] font-semibold ${salMode==="mois"?"bg-blue-600 text-white":"bg-white text-slate-600 hover:bg-slate-50"}`}>Mois</button>
              <button data-testid="sal-evol-semaine" onClick={()=>setSalMode("semaine")}
                      className={`px-2.5 py-1 text-[11px] font-semibold ${salMode==="semaine"?"bg-blue-600 text-white":"bg-white text-slate-600 hover:bg-slate-50"}`}>Semaine</button>
            </div>
          </div>
          <div className="h-56">
            <ResponsiveContainer>
              <ComposedChart data={evoData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9"/>
                <XAxis dataKey="mois" tick={{ fontSize: 10, fill: "#64748B" }} axisLine={false} tickLine={false}/>
                <YAxis yAxisId="left" tickFormatter={fmtEurShort} tick={{ fontSize: 10, fill: "#64748B" }} axisLine={false} tickLine={false} width={50}/>
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: "#64748B" }} tickFormatter={v => v + "%"} axisLine={false} tickLine={false} width={35}/>
                <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 6, fontSize: 12 }}/>
                <Legend wrapperStyle={{ fontSize: 10 }} iconType="rect"/>
                <Bar yAxisId="left" dataKey="Masse salariale brute" fill="#DBEAFE" radius={[4, 4, 0, 0]}/>
                <Line yAxisId="right" type="monotone" dataKey="Taux MS (%)" stroke={CLR.violet} strokeWidth={2.5} dot={{ r: 3 }}/>
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="sal-repartition">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-bold text-slate-800 text-sm">Répartition de la masse salariale</h3>
          </div>
          <div className="flex bg-slate-100 rounded-lg p-0.5 mb-3">
            {[{ k: "contrat", l: "Par type de contrat" }, { k: "poste", l: "Par poste" }, { k: "region", l: "Par région" }].map(t => (
              <button key={t.k} onClick={() => setRepartMode(t.k)}
                      className={`flex-1 text-[10px] px-2 py-1 rounded-md font-semibold ${repartMode === t.k ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}>
                {t.l}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-4">
            <div className="w-32 h-32 relative flex-shrink-0">
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={repartition.slice(0, 6)} dataKey="value" nameKey="name" innerRadius={35} outerRadius={58} paddingAngle={2}>
                    {repartition.slice(0, 6).map((e, i) => <Cell key={i} fill={CONTRAT_COLORS[e.name] || `hsl(${i * 47}, 70%, 55%)`}/>)}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 grid place-items-center pointer-events-none">
                <div className="text-center">
                  <div className="text-[13px] font-bold text-slate-900 tabular-nums">{fmtEurShort(totalMs)}</div>
                  <div className="text-[8px] uppercase font-bold text-slate-500">masse brute</div>
                </div>
              </div>
            </div>
            <div className="flex-1 space-y-1 text-[10px]">
              {repartition.slice(0, 6).map(d => (
                <div key={d.name} className="grid grid-cols-[8px_1fr_35px_50px] items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full" style={{ background: CONTRAT_COLORS[d.name] || "#94A3B8" }}/>
                  <span className="text-slate-700 font-medium truncate">{d.name}</span>
                  <span className="text-slate-900 font-bold tabular-nums text-right">{totalMs ? Math.round(d.value / totalMs * 100) : 0}%</span>
                  <span className="text-slate-500 tabular-nums text-right">{fmtEurShort(d.value)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="sal-cout-poste">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-slate-800 text-sm">Coût employeur par poste</h3>
            <span className="text-[9px] text-slate-500 italic">Coût moyen / mois</span>
          </div>
          <div className="space-y-1.5">
            {coutParPoste.map(c => (
              <div key={c.poste} className="grid grid-cols-[110px_60px_1fr] items-center gap-3 text-[11px]">
                <div className="flex items-center gap-1.5">
                  <Users className="w-3 h-3 text-slate-400"/>
                  <span className="text-slate-700 font-medium truncate">{c.poste}</span>
                </div>
                <span className="text-slate-900 font-bold tabular-nums text-right">{fmtEurShort(c.avg)}</span>
                <div className="h-3 rounded bg-slate-100 overflow-hidden">
                  <div className="h-full rounded" style={{ width: `${(c.avg / maxCout) * 100}%`, background: CLR.blue }}/>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <SalairesDetailTable collabs={collabs}/>

      {/* Bottom widgets */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        {[
          { icon: TrendingUp, color: "blue", title: "Simulation d'augmentation", desc: "Testez l'impact d'une revalorisation salariale" },
          { icon: Award, color: "emerald", title: "Benchmark marché", desc: "Comparez avec les salaires du secteur" },
          { icon: AlertTriangle, color: "rose", title: "Alertes & anomalies", desc: `${Math.round(collabs.length * 0.03)} collaborateurs à surveiller` },
          { icon: Euro, color: "violet", title: "Budget prévisionnel", desc: "Projetez votre masse salariale" },
        ].map(w => (
          <button key={w.title} className={`bg-white rounded-xl border border-${w.color}-200 p-4 text-left hover:shadow-md transition`} data-testid={`sal-widget-${w.color}`}>
            <div className={`w-8 h-8 rounded-lg bg-${w.color}-100 grid place-items-center mb-2`}>
              <w.icon className={`w-4 h-4 text-${w.color}-600`}/>
            </div>
            <div className="font-bold text-slate-900 text-xs mb-0.5">{w.title}</div>
            <div className="text-[10px] text-slate-500">{w.desc}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ─── Sub: Salaires Detail Table (virtualized) ───────────────────────── */
const SalairesRow = ({ index, style, rows }) => {
  const c = rows[index];
  if (!c) return null;
  const initials = (c.name || "").split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div style={{ ...style, display: "grid", gridTemplateColumns: "1.5fr 1fr 0.9fr 0.7fr 0.6fr 0.6fr 0.7fr 0.7fr 0.8fr 0.5fr 0.7fr 0.7fr 0.6fr" }}
         className="items-center text-[11px] border-b border-slate-100 hover:bg-slate-50/70 px-3">
      <div className="flex items-center gap-2 min-w-0">
        <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 grid place-items-center font-bold text-[9px] shrink-0">{initials}</span>
        <span className="font-semibold text-slate-900 truncate">{c.name}</span>
      </div>
      <div className="text-slate-600 truncate">{c.corner}</div>
      <div className="text-slate-600 truncate">{c.poste}</div>
      <div className="text-slate-600">{c.contrat}</div>
      <div className="text-right tabular-nums">{fmtNum(c.heures_mois, 0)} h</div>
      <div className="text-right tabular-nums">{fmtEurH(c.cout_h)}</div>
      <div className="text-right tabular-nums">{fmtEur(c.salaire_brut)}</div>
      <div className="text-right tabular-nums text-slate-600">{fmtEur(c.charges)}</div>
      <div className="text-right tabular-nums font-semibold">{fmtEur(c.cout_emp)}</div>
      <div className="text-right tabular-nums">{fmtNum(c.etp, 2)}</div>
      <div className="text-right tabular-nums">{fmtEurH(c.cout_h)}</div>
      <div className="text-right tabular-nums text-slate-600">{c.anciennete} ans</div>
      <div className="text-right">
        <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-emerald-100 text-emerald-800">{c.statut}</span>
      </div>
    </div>
  );
};

function SalairesDetailTable({ collabs }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    if (!qq) return collabs;
    return collabs.filter(c => (c.name || "").toLowerCase().includes(qq) || (c.corner || "").toLowerCase().includes(qq) || (c.poste || "").toLowerCase().includes(qq));
  }, [collabs, q]);
  const listHeight = Math.min(360, Math.max(80, filtered.length * 36 + 8));

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="sal-detail-table">
      <div className="p-3 border-b border-slate-100 flex items-center justify-between">
        <div>
          <h2 className="font-bold text-slate-900 text-sm">Détail des salaires par collaborateur</h2>
          <p className="text-[10px] text-slate-500">{filtered.length} collaborateurs</p>
        </div>
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"/>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Rechercher un collaborateur…"
                 className="pl-8 pr-3 py-1.5 rounded-lg border border-slate-200 text-xs w-64 outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400"/>
        </div>
      </div>
      <div className="grid grid-cols-[1.5fr_1fr_0.9fr_0.7fr_0.6fr_0.6fr_0.7fr_0.7fr_0.8fr_0.5fr_0.7fr_0.7fr_0.6fr] border-b-2 border-slate-200 bg-slate-50 text-[8px] font-bold uppercase tracking-wider text-slate-500 px-3 py-2">
        <div>Nom</div><div>Corner</div><div>Poste</div><div>Contrat</div>
        <div className="text-right">Heures</div><div className="text-right">Taux</div>
        <div className="text-right">Salaire brut</div><div className="text-right">Charges</div><div className="text-right">Coût emp.</div>
        <div className="text-right">ETP</div><div className="text-right">Coût/h</div><div className="text-right">Ancienneté</div><div className="text-right">Statut</div>
      </div>
      <div style={{ height: listHeight }}>
        <RwList rowCount={filtered.length} rowHeight={36} defaultHeight={listHeight}
                rowComponent={SalairesRow} rowProps={{ rows: filtered }}/>
      </div>
    </div>
  );
}

/* ─── Collaborateurs tab — live sheet with CV + history + objectives ── */
function CollaborateursTab({ rhRows }) {
  const [collabs, setCollabs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    setLoading(true);
    api.get("/kpi/rh/collaborateurs").then(r => setCollabs(r.data || [])).catch(() => setCollabs([])).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    if (!qq) return collabs;
    return collabs.filter(c =>
      (c.name || "").toLowerCase().includes(qq) ||
      (c.corner || "").toLowerCase().includes(qq) ||
      (c.poste || "").toLowerCase().includes(qq)
    );
  }, [collabs, q]);

  return (
    <div className="space-y-4" data-testid="collab-tab">
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-3 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-bold text-slate-900 text-sm">Fiches Collaborateurs</h2>
            <p className="text-[10px] text-slate-500">{filtered.length} / {collabs.length} collaborateurs · Cliquez sur une fiche pour voir le détail</p>
          </div>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"/>
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Rechercher un collaborateur, corner, poste…"
                   className="pl-8 pr-3 py-1.5 rounded-lg border border-slate-200 text-xs w-72 outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400"
                   data-testid="collab-search"/>
          </div>
        </div>
        {loading && <div className="p-6 text-center text-sm text-slate-400 animate-pulse">Chargement des collaborateurs…</div>}
        {!loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 p-3 max-h-[600px] overflow-y-auto">
            {filtered.slice(0, 60).map(c => {
              const initials = (c.name || "").split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();
              const contrat = classifyContrat(c.poste);
              return (
                <button key={c.name} onClick={() => setSelected(c.name)}
                        data-testid={`collab-card-${c.name}`}
                        className="text-left bg-white border border-slate-200 rounded-lg p-3 hover:border-blue-400 hover:shadow-md transition">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-9 h-9 rounded-full bg-blue-100 text-blue-700 grid place-items-center font-bold text-xs">{initials}</span>
                    <div className="min-w-0 flex-1">
                      <div className="font-bold text-slate-900 text-xs truncate">{c.name}</div>
                      <div className="text-[10px] text-slate-500 truncate">{c.poste || "—"}</div>
                    </div>
                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ background: (CONTRAT_COLORS[contrat] || "#94A3B8") + "20", color: CONTRAT_COLORS[contrat] || "#334155" }}>{contrat}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-1 text-[10px] mb-1.5">
                    <div className="bg-slate-50 rounded px-1.5 py-1">
                      <div className="font-bold uppercase text-slate-400 text-[8px]">Corner</div>
                      <div className="font-semibold text-slate-800 truncate">{c.corner || "—"}</div>
                    </div>
                    <div className="bg-slate-50 rounded px-1.5 py-1">
                      <div className="font-bold uppercase text-slate-400 text-[8px]">Salaire</div>
                      <div className="font-semibold text-slate-800 tabular-nums">{fmtEur(c.salaire_brut)}</div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-[9px] text-slate-500">
                    <span>{c.dr || "—"}</span>
                    <span>{c.n_objectives ? `${c.n_objectives} obj.` : "0 obj."}</span>
                  </div>
                </button>
              );
            })}
            {filtered.length === 0 && !loading && <div className="col-span-full text-center py-8 text-slate-400 italic text-sm">Aucun collaborateur</div>}
            {filtered.length > 60 && <div className="col-span-full text-center py-2 text-[10px] text-slate-400 italic">Affiche 60 sur {filtered.length} — précisez la recherche pour affiner.</div>}
          </div>
        )}
      </div>
      {selected && <CollaborateurDrawer name={selected} onClose={() => setSelected(null)}/>}
    </div>
  );
}

/* ─── Drawer avec détail complet du collaborateur ─────────────────────── */
function CollaborateurDrawer({ name, onClose }) {
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("apercu"); // apercu | historique | objectifs | cv
  const [objectives, setObjectives] = useState([]);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!name) return;
    setD(null);
    api.get(`/kpi/rh/collaborateur/${encodeURIComponent(name)}`)
       .then(r => { setD(r.data); setObjectives(r.data.objectives || []); setNotes(r.data.notes || ""); })
       .catch(() => setD({ error: true }));
  }, [name]);

  const addObjective = () => setObjectives(o => [...o, { title: "Nouvel objectif", description: "", deadline: new Date().toISOString().slice(0, 10), status: "in_progress", progress: 0 }]);
  const updateObj = (i, patch) => setObjectives(o => o.map((x, j) => j === i ? { ...x, ...patch } : x));
  const removeObj = (i) => setObjectives(o => o.filter((_, j) => j !== i));
  const saveObjectives = async () => {
    setSaving(true);
    try {
      await api.post("/kpi/rh/collaborateur/objectives", { name, objectives, notes });
      toast.success("Objectifs sauvegardés");
    } catch { toast.error("Erreur lors de la sauvegarde"); }
    setSaving(false);
  };

  if (!d) return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="collab-drawer-loading">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}/>
      <div className="relative bg-white w-full max-w-2xl h-full shadow-2xl grid place-items-center">
        <div className="text-sm text-slate-500 animate-pulse">Chargement…</div>
      </div>
    </div>
  );
  if (d.error) return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}/>
      <div className="relative bg-white w-full max-w-2xl h-full shadow-2xl grid place-items-center p-6">
        <div className="text-center">
          <AlertTriangle className="w-8 h-8 text-rose-500 mx-auto mb-2"/>
          <div className="text-sm text-slate-700 font-semibold">Impossible de charger la fiche</div>
          <button onClick={onClose} className="mt-4 px-4 py-1.5 rounded-lg bg-slate-100 text-slate-700 text-xs font-semibold">Fermer</button>
        </div>
      </div>
    </div>
  );

  const initials = (d.name || "").split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();
  const c = d.current;
  const contrat = classifyContrat(c.poste);

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="collab-drawer">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}/>
      <div className="relative bg-white w-full max-w-2xl h-full shadow-2xl flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-slate-200 flex items-start justify-between gap-3 bg-gradient-to-br from-blue-50 to-white">
          <div className="flex items-start gap-3 min-w-0">
            <span className="w-14 h-14 rounded-full bg-blue-600 text-white grid place-items-center font-bold text-lg shrink-0">{initials}</span>
            <div className="min-w-0">
              <h2 className="font-display font-bold text-slate-900 text-lg truncate">{d.name}</h2>
              <p className="text-[12px] text-slate-600 truncate">{c.poste || "—"} · {c.corner || "—"}</p>
              <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ background: (CONTRAT_COLORS[contrat] || "#94A3B8") + "20", color: CONTRAT_COLORS[contrat] || "#334155" }}>{contrat}</span>
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">{d.totals.anciennete_mois} mois d'ancienneté</span>
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">Actif</span>
              </div>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded" data-testid="collab-drawer-close"><XIcon className="w-4 h-4"/></button>
        </div>

        {/* Tabs */}
        <div className="flex items-center border-b border-slate-200 px-4">
          {[
            { k: "apercu", l: "Aperçu" },
            { k: "historique", l: "Historique" },
            { k: "objectifs", l: `Objectifs (${objectives.length})` },
            { k: "cv", l: "CV" },
          ].map(t => (
            <button key={t.k} onClick={() => setTab(t.k)}
                    data-testid={`collab-drawer-tab-${t.k}`}
                    className={`px-3 py-2 text-[11px] font-semibold border-b-2 ${tab === t.k ? "border-blue-600 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
              {t.l}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {tab === "apercu" && (
            <>
              <div className="grid grid-cols-2 gap-2">
                <InfoCard label="Corner actuel" value={c.corner || "—"}/>
                <InfoCard label="Poste" value={c.poste || "—"}/>
                <InfoCard label="Manager (BM)" value={c.bm || "—"}/>
                <InfoCard label="Directeur régional" value={c.dr || "—"}/>
                <InfoCard label="Région" value={c.region || "—"}/>
                <InfoCard label="Enseigne" value={c.enseigne || "—"}/>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <StatCard label="Salaire brut" value={fmtEur(c.salaire_brut)} icon={Euro} color="emerald"/>
                <StatCard label="Taux horaire" value={fmtEurH(c.taux_horaire)} icon={Clock} color="cyan"/>
                <StatCard label="ETP" value={fmtNum(c.etp_semaine, 2)} icon={Users} color="blue"/>
              </div>
              <div className="bg-slate-50 rounded-lg p-3">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">Cumuls sur la période</div>
                <div className="grid grid-cols-4 gap-2 text-center">
                  <div><div className="text-[9px] text-slate-500">Heures travaillées</div><div className="font-bold text-slate-900 tabular-nums">{fmtH(d.totals.h_travaillees)}</div></div>
                  <div><div className="text-[9px] text-slate-500">Heures sup.</div><div className="font-bold text-slate-900 tabular-nums">{fmtH(d.totals.hs)}</div></div>
                  <div><div className="text-[9px] text-slate-500">Absences</div><div className="font-bold text-slate-900 tabular-nums">{fmtH(d.totals.habs)}</div></div>
                  <div><div className="text-[9px] text-slate-500">Masse salariale</div><div className="font-bold text-slate-900 tabular-nums">{fmtEurShort(d.totals.masse_salariale)}</div></div>
                </div>
              </div>
              {d.monthly.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-lg p-3">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">Évolution mensuelle des heures travaillées</div>
                  <div className="h-40">
                    <ResponsiveContainer>
                      <LineChart data={d.monthly.map(m => ({ mois: MONTH_ABBR[m.mois] || m.mois, "Heures": Math.round(m.h_travaillees), "MS": Math.round(m.masse_salariale) }))}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9"/>
                        <XAxis dataKey="mois" tick={{ fontSize: 9 }} axisLine={false} tickLine={false}/>
                        <YAxis tick={{ fontSize: 9 }} axisLine={false} tickLine={false} width={40}/>
                        <Tooltip contentStyle={{ fontSize: 11 }}/>
                        <Line type="monotone" dataKey="Heures" stroke={CLR.blue} strokeWidth={2} dot={{ r: 3 }}/>
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </>
          )}
          {tab === "historique" && (
            <>
              <div>
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">Historique de poste ({d.poste_history.length})</div>
                <div className="space-y-2">
                  {d.poste_history.map((p, i) => (
                    <div key={i} className="flex items-center gap-3 bg-white border border-slate-200 rounded-lg p-2.5">
                      <span className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 grid place-items-center shrink-0"><Award className="w-4 h-4"/></span>
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-slate-900 text-sm truncate">{p.poste || "—"}</div>
                        <div className="text-[10px] text-slate-500">Depuis le {p.date_debut} · {p.corner}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2 mt-4">Historique de corner ({d.corner_history.length})</div>
                <div className="space-y-2">
                  {d.corner_history.map((cn, i) => (
                    <div key={i} className="flex items-center gap-3 bg-white border border-slate-200 rounded-lg p-2.5">
                      <span className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center shrink-0"><LayoutGrid className="w-4 h-4"/></span>
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-slate-900 text-sm truncate">{cn.corner || "—"}</div>
                        <div className="text-[10px] text-slate-500">Depuis le {cn.date_debut} · {cn.enseigne} · {cn.secteur}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2 mt-4">Détail mensuel ({d.monthly.length} mois)</div>
                <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                  <table className="w-full text-[11px]">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="text-left px-2 py-1.5 font-bold text-slate-500 text-[9px] uppercase">Mois</th>
                        <th className="text-right px-2 py-1.5 font-bold text-slate-500 text-[9px] uppercase">H. trav.</th>
                        <th className="text-right px-2 py-1.5 font-bold text-slate-500 text-[9px] uppercase">HS</th>
                        <th className="text-right px-2 py-1.5 font-bold text-slate-500 text-[9px] uppercase">HABS</th>
                        <th className="text-right px-2 py-1.5 font-bold text-slate-500 text-[9px] uppercase">MS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.monthly.map((m, i) => (
                        <tr key={i} className="border-b border-slate-100">
                          <td className="px-2 py-1 text-slate-700">{m.mois} <span className="text-slate-400">{m.exercice}</span></td>
                          <td className="text-right px-2 py-1 tabular-nums font-semibold">{fmtNum(m.h_travaillees, 1)}</td>
                          <td className="text-right px-2 py-1 tabular-nums">{fmtNum(m.hs, 1)}</td>
                          <td className="text-right px-2 py-1 tabular-nums">{fmtNum(m.habs, 1)}</td>
                          <td className="text-right px-2 py-1 tabular-nums font-semibold">{fmtEur(m.masse_salariale)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
          {tab === "objectifs" && (
            <>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-slate-900 text-sm">Objectifs personnels</h3>
                  <p className="text-[10px] text-slate-500">Définissez et suivez les objectifs de {d.name}</p>
                </div>
                <button onClick={addObjective} data-testid="collab-add-objective"
                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-blue-600 text-white text-[11px] font-bold hover:bg-blue-700">
                  <UserPlus className="w-3.5 h-3.5"/> Ajouter
                </button>
              </div>
              <div className="space-y-2">
                {objectives.map((o, i) => (
                  <div key={i} className="bg-white border border-slate-200 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <input value={o.title} onChange={e => updateObj(i, { title: e.target.value })}
                             className="flex-1 px-2 py-1 border-0 border-b border-transparent hover:border-slate-200 focus:border-blue-400 outline-none font-bold text-sm text-slate-900"/>
                      <SearchSelect value={o.status} onChange={v => updateObj(i, { status: v })} options={[{ value: "in_progress", label: "En cours" }, { value: "done", label: "Atteint" }, { value: "blocked", label: "Bloqué" }]} size="lg" menuWidth={260}/>
                      <button onClick={() => removeObj(i)} className="text-rose-500 hover:bg-rose-50 p-1 rounded"><XIcon className="w-3.5 h-3.5"/></button>
                    </div>
                    <textarea value={o.description} onChange={e => updateObj(i, { description: e.target.value })}
                              placeholder="Description de l'objectif…"
                              className="w-full px-2 py-1 rounded border border-slate-200 text-[11px] mb-2 min-h-[50px] resize-none"/>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-[9px] font-bold uppercase text-slate-500">Deadline</label>
                        <input type="date" value={o.deadline || ""} onChange={e => updateObj(i, { deadline: e.target.value })}
                               className="w-full px-2 py-1 rounded border border-slate-200 text-[11px]"/>
                      </div>
                      <div>
                        <label className="text-[9px] font-bold uppercase text-slate-500">Progrès : {o.progress || 0}%</label>
                        <input type="range" min={0} max={100} step={5} value={o.progress || 0} onChange={e => updateObj(i, { progress: +e.target.value })}
                               className="w-full accent-blue-600"/>
                      </div>
                    </div>
                  </div>
                ))}
                {objectives.length === 0 && (
                  <div className="text-center py-8 text-[11px] text-slate-400 italic border-2 border-dashed border-slate-200 rounded-lg">
                    Aucun objectif défini. Cliquez sur "Ajouter" pour en créer un.
                  </div>
                )}
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase text-slate-500 mb-1 block">Notes managériales</label>
                <textarea value={notes} onChange={e => setNotes(e.target.value)}
                          placeholder="Notes libres (points forts, axes de progression, formations envisagées…)"
                          className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[11px] min-h-[70px] resize-none"/>
              </div>
              <div className="flex justify-end pt-2 border-t border-slate-100">
                <button onClick={saveObjectives} disabled={saving} data-testid="collab-save-objectives"
                        className="px-4 py-1.5 rounded-lg bg-blue-600 text-white text-[11px] font-bold hover:bg-blue-700 disabled:opacity-50">
                  {saving ? "Sauvegarde…" : "Sauvegarder"}
                </button>
              </div>
            </>
          )}
          {tab === "cv" && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 text-center">
              <Award className="w-10 h-10 text-slate-300 mx-auto mb-3"/>
              <div className="font-bold text-slate-800 text-sm mb-1">CV du collaborateur</div>
              <p className="text-[11px] text-slate-500 mb-3">Ajoutez expérience professionnelle, formations et compétences.</p>
              <p className="text-[10px] text-slate-400 italic">Fonctionnalité d'upload CV bientôt disponible — pour l'instant, ajoutez les informations libres dans l'onglet Objectifs / Notes.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function InfoCard({ label, value }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-2">
      <div className="text-[9px] font-bold uppercase tracking-wider text-slate-400">{label}</div>
      <div className="font-semibold text-slate-900 text-[12px] truncate">{value}</div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color }) {
  return (
    <div className={`bg-${color}-50 rounded-lg p-2 border border-${color}-100`}>
      <div className="flex items-center gap-1.5 mb-0.5">
        <Icon className={`w-3 h-3 text-${color}-600`}/>
        <span className="text-[9px] font-bold uppercase text-slate-500">{label}</span>
      </div>
      <div className="font-bold text-slate-900 text-[13px] tabular-nums">{value}</div>
    </div>
  );
}

/* ─── Collaborateurs tab (legacy placeholder — replaced) ─────────────── */
function CollaborateursTabPlaceholder() {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-16 text-center">
      <Users className="w-16 h-16 mx-auto text-slate-300 mb-4"/>
      <h3 className="font-bold text-slate-800 text-lg mb-2">Espace Collaborateurs</h3>
    </div>
  );
}

/* ─── Main Page ──────────────────────────────────────────────────────── */
export default function KpiRHV2() {
  const [filters, setFilters] = useState(null);
  const [f, setF] = useSharedKpiFilters({ drs: [], secteurs: [], corners: [], exercice: null, mois: [], semaines: [] }, { meta: filters, pageKey: "orbit_rh_v2_filters" });

  const [rhRows, setRhRows] = useState([]);
  const [prodData, setProdData] = useState(null);
  const [chargesSalaires, setChargesSalaires] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("tab") || "vue-rh");
  const [simChoice, setSimChoice] = useState(() => new URLSearchParams(window.location.search).get("sim") || null);
  const [importOpen, setImportOpen] = useState(false);
  const location = useLocation();
  useEffect(() => { const u = new URLSearchParams(location.search); setTab(u.get("tab") || "vue-rh"); setSimChoice(u.get("sim") || null); }, [location.search]);

  useEffect(() => {
    // Merge distinct filter options from both kpi_rh (masse salariale) and kpi_prod (productivité).
    // Sources indépendantes, mais on unionne pour proposer les valeurs présentes dans l'un ou l'autre.
    Promise.all([
      api.get("/kpi/rh/meta").then(r => r.data).catch(() => ({})),
      api.get("/kpi/productivite/meta").then(r => r.data).catch(() => ({})),
    ]).then(([rh, prod]) => {
      const merge = (a = [], b = []) => Array.from(new Set([...(a || []), ...(b || [])])).filter(Boolean).sort();
      const cornersMap = {};
      [...(rh.corners || []), ...(prod.corners || [])].forEach(c => {
        if (!c.corner_key) return;
        cornersMap[c.corner_key] = { ...(cornersMap[c.corner_key] || {}), ...c };
      });
      setFilters({
        drs: merge(rh.drs, prod.drs),
        secteurs: merge(rh.secteurs, prod.secteurs),
        bms: merge(rh.bms, prod.bms),
        enseignes: merge(rh.enseignes, prod.enseignes),
        regions: merge(rh.regions, prod.regions),
        semaines: merge(rh.semaines, prod.semaines),
        exercices: merge(rh.exercices, prod.exercices),
        mois: merge(rh.mois, prod.mois),
        corners: Object.values(cornersMap).sort((x, y) => (x.corner || "").localeCompare(y.corner || "")),
      });
    });
  }, []);
  useEffect(() => { api.get("/kpi/charges-salaires").then(r => setChargesSalaires(r.data || [])).catch(() => {}); }, []);

  useEffect(() => {
    setLoading(true);
    const params = toParams({ drs: f.drs, secteurs: f.secteurs, corners: f.corners, semaines: f.semaines });
    if (f.exercice) params.exercice = f.exercice;
    Promise.all([
      api.get("/kpi/rh/rows", { params }).then(r => r.data || []).catch(() => []),
      api.get("/kpi/productivite", { params }).then(r => r.data || {}).catch(() => ({})),
    ]).then(([rh, prod]) => {
      setRhRows(rh);
      setProdData(prod);
    }).finally(() => setLoading(false));
  }, [f.drs, f.secteurs, f.corners, f.exercice, f.semaines]);

  const filteredRows = useMemo(() => {
    let rows = rhRows;
    if (f.mois?.length) {
      // Compare accent+case insensitive : stored can be "AOUT", "AOÛT", "Août"...
      const wanted = new Set(f.mois.map(_foldFR));
      rows = rows.filter(r => wanted.has(_foldFR(r.mois)));
    }
    if (f.semaines?.length) rows = rows.filter(r => f.semaines.includes(r.semaine));
    return rows;
  }, [rhRows, f.mois, f.semaines]);

  const exercicesFromData = useMemo(() => [...new Set(rhRows.map(r => r.exercice).filter(Boolean))].sort(), [rhRows]);

  // Monthly aggregation (for KPI variations)
  const monthAgg = useMemo(() => {
    const map = {};
    rhRows.forEach(r => {
      const key = r.mois; if (!key) return;
      map[key] = map[key] || { mois: key, rows: [] };
      map[key].rows.push(r);
    });
    const list = Object.entries(map)
      .map(([mois, v]) => ({ mois, ...aggRh(v.rows), rows_count: v.rows.length, order: MONTHS_FR.indexOf(mois) }))
      .sort((a, b) => a.order - b.order);
    const monthsInData = list.map(m => m.mois);
    if (isMultiMonth(f)) {
      // Année / exercice / plusieurs mois / dates perso → cumul de la période filtrée (= tableaux) ; pas de comparaison inventée
      const cur = filteredRows.length ? { mois: "PERIODE", ...aggRh(filteredRows), rows_count: filteredRows.length, ca: prodData?.summary?.ca_ht || 0 } : null;
      return { list, cur, prev: null };
    }
    const target = f.mois?.[0] || monthsInData[monthsInData.length - 1];
    const idx = monthsInData.indexOf(target);
    const cur = idx >= 0 ? list[idx] : (list[list.length - 1] || null);
    const prev = idx > 0 ? list[idx - 1] : (list[list.length - 2] || null);
    // Add ca to cur/prev
    if (cur) cur.ca = prodData?.summary?.ca_ht || 0;
    return { list, cur, prev };
  }, [rhRows, f.mois, prodData, filteredRows, f.exercice, f.annees, f.date_from, f.date_to]); // eslint-disable-line react-hooks/exhaustive-deps

  // Weekly aggregation (Semaine mode for widgets)
  const weekAgg = useMemo(() => {
    const map = {};
    filteredRows.forEach(r => {
      const w = (r.semaine || "").toString().trim(); if (!w) return;
      map[w] = map[w] || { semaine: w, rows: [] }; map[w].rows.push(r);
    });
    return Object.entries(map)
      .map(([semaine, v]) => ({ semaine, ...aggRh(v.rows) }))
      .sort((a, b) => a.semaine.localeCompare(b.semaine, undefined, { numeric: true }));
  }, [filteredRows]);

  const tabs = [
    { k: "vue-rh", l: "Vue RH", icon: LayoutGrid },
    { k: "prod", l: "Productivité", icon: TrendingUp },
    { k: "prod-analyse", l: "Analyse", icon: Search },
    { k: "simulateur", l: "Simulateur", icon: Play },
    { k: "salaires", l: "Salaires", icon: Euro },
    { k: "collab", l: "Collaborateurs", icon: Users },
  ];
  const currentTab = tabs.find(t => t.k === tab);
  const subtitles = {
    "vue-rh": "Suivi de la masse salariale · Effectifs · Coûts · Performance des équipes",
    "prod": "Productivité · CA / heure travaillée · Performance opérationnelle",
    "prod-analyse": "Analyse · Comprendre les écarts de productivité : récurrence, CA vs heures, croisement RH, diagnostic par corner",
    "simulateur": "Simulateur de Productivité & Masse salariale · Prenez la bonne décision avant d'agir",
    "salaires": "Suivi des salaires · Coûts employeur · Équité interne · Pilotage budgétaire",
    "collab": "Fiches individuelles et parcours",
  };

  const onExport = () => {
    if (!filteredRows.length) { toast.error("Aucune ligne à exporter"); return; }
    const cols = ["corner", "region", "dr", "secteur", "bm", "name", "poste", "mois", "semaine", "h_travaillees", "hs", "habs", "masse_salariale", "salaire_brut", "taux_horaire", "etp_semaine"];
    const csv = ["\ufeff" + cols.map(c => `"${c}"`).join(";")];
    filteredRows.forEach(r => csv.push(cols.map(c => `"${String(r[c] ?? "").replace(/"/g, '""')}"`).join(";")));
    const url = URL.createObjectURL(new Blob([csv.join("\r\n")], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a"); a.href = url; a.download = `orbit_rh_${tab}.csv`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    toast.success(`Export réussi — ${filteredRows.length} lignes`);
  };

  return (
    <div className="space-y-4" data-testid="kpi-rh-v2-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-600 text-white grid place-items-center font-bold">
            <Users className="w-5 h-5"/>
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 uppercase leading-none">RH</h1>
            <p className="text-[11px] text-slate-500 mt-0.5">{subtitles[tab]}</p>
            <LastImportInfo kinds={["rh"]} className="mt-1.5"/><CreateReportButton module="rh" className="mt-2 ml-2"/><Link to="/kpi/rh/hs" className="inline-flex items-center gap-1 mt-2 ml-2 text-[12px] font-semibold px-3 py-1.5 rounded-lg bg-orange-500 text-white hover:bg-orange-600" data-testid="rh-hs-analyse-link">Analyser les heures supplémentaires →</Link>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Link to="/admin/import/rh" data-testid="rh-import-link" className="text-[11px] font-semibold text-blue-700 hover:underline">Imports → Administration</Link>
          <button className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-600" data-testid="rh-settings-btn">
            <Settings className="w-3.5 h-3.5"/>
          </button>
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex items-center gap-1 border-b border-slate-200 flex-wrap" data-testid="rh-tab-bar">
        {tabs.map(t => {
          const active = tab === t.k;
          return (
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`rh-tab-${t.k}`}
                    className={`flex items-center gap-1.5 px-4 py-2 text-[12px] font-semibold border-b-2 transition ${active ? "border-blue-600 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
              <t.icon className="w-3.5 h-3.5"/>
              <span>{t.l}</span>
            </button>
          );
        })}
        <PageActions title={`RH · ${currentTab?.l || ""}`} filters={() => document.querySelector("[data-testid=rh-filters]")?.innerText?.replace(/\s+/g, " ").slice(0, 160) || ""} onAi={scrollToAiBlock} aiLabel={`Analyse IA ${tab === "vue-rh" ? "RH" : tab === "prod" ? "Productivité" : tab === "salaires" ? "Salaires" : ""}`} onExcel={onExport} testid="rh-actions" />
      </div>

      {/* Filters partagés — masqués pour Vue RH et Productivité qui ont leur propre FilterBar */}
      {tab !== "vue-rh" && tab !== "prod" && tab !== "prod-analyse" && !(tab === "simulateur" && (simChoice === "productivite" || simChoice === "embauche" || simChoice === "heures_sup" || !simChoice)) && (
        <FilterBar filters={filters} f={f} setF={setF} exercicesFromData={exercicesFromData}/>
      )}

      {/* Tab content — Vue RH et Prod sont autonomes (filtres + data internes) */}
      {tab === "vue-rh" && <VueRHTab/>}
      {tab === "prod" && <ProductiviteTab/>}
      {tab === "prod-analyse" && <ProdAnalyse/>}
      {tab === "simulateur" && (
        simChoice === "productivite" ? <ProductiviteSimulator onBack={() => setSimChoice(null)}/>
        : simChoice === "embauche" ? <EmbaucheSimulator onBack={() => setSimChoice(null)}/>
        : simChoice === "heures_sup" ? <HeuresSupSimulator onBack={() => setSimChoice(null)} onOpenHiring={(ctx) => { try { localStorage.setItem("orbit_rh_prod_filters", JSON.stringify(ctx.filters || {})); localStorage.setItem("orbit_hire_ctx", JSON.stringify(ctx)); } catch {} setSimChoice("embauche"); }}/>
        : <SimulateurLanding onPick={setSimChoice}/>
      )}
      {tab === "salaires" && <SalairesTab rhRows={filteredRows} prodData={prodData} monthAgg={monthAgg} weekAgg={weekAgg} chargesSalaires={chargesSalaires}/>}
      {tab === "collab" && <CollaborateursTab rhRows={filteredRows}/>}

      {loading && <div className="fixed bottom-4 right-4 bg-slate-900 text-white text-xs px-3 py-1.5 rounded-lg shadow-lg animate-pulse">Chargement…</div>}
    </div>
  );
}
